// src/client/RecommendationClient.ts
import {
  joinPath as joinPath2,
  run as run7,
  isObjectGuard as isObjectGuard3,
  isNumberGuard,
  isStringGuard,
  isItemInArrayGuard
} from "@crossingminds/utils";

// src/errors/ClientError.ts
var ClientErrorMessages = /* @__PURE__ */ ((ClientErrorMessages2) => {
  ClientErrorMessages2["noFetchClient"] = "No fetch client available";
  ClientErrorMessages2["dataMismatch"] = "Unexpected data shape";
  ClientErrorMessages2["localStorageNotAvailable"] = "Local Storage not available";
  ClientErrorMessages2["noCredentialsPresent"] = "No credentials present";
  ClientErrorMessages2["couldNotParseJSON"] = "Could not parse response JSON";
  ClientErrorMessages2["accountRoleNotAllowed"] = "Account role not allowed";
  ClientErrorMessages2["allowedAccountRolesNotProvided"] = "Allowed account roles not provided";
  return ClientErrorMessages2;
})(ClientErrorMessages || {});
var causes = {
  "No fetch client available": "The fetch standard has not been implemented. You can provide your own fetch function (pony-fill) by specifying the `fetchClient` property, or by setting the `globalThis.fetch` property with a polyfill like isomorphic-fetch.",
  "Unexpected data shape": "The data received did not match the shape (type) expected by the client. Using the latest version of this library may fix this problem.",
  "Local Storage not available": 'Local Storage was not found at "globalThis.localStorage". This feature is typically only available in browsers. You can polyfill the property, or create your own storage handler with the "onUpdateCredentials" event',
  "No credentials present": "RecommendationClient does not contain have auth credentials. Use a command that returns auth credentials to re-authenticate",
  "Could not parse response JSON": "Client failed while trying to parse JSON returned by network request",
  "Account role not allowed": "RecommendationClient has a required property called `allowedAccountRoles`. This property determines which types of accounts the client is allowed to login with.",
  "Allowed account roles not provided": "Property `allowedAccountRoles` is required when instantiating `RecommendationClient`"
};
var ClientError = class extends Error {
  name = "ClientError";
  constructor({ message, errorPayload, commandId }) {
    super(message, { cause: causes[message] });
    this.basicMessage = message;
    if (errorPayload) {
      this.errorPayload = errorPayload;
    }
    if (commandId) {
      this.commandId = commandId;
    }
    this.message = this.#createComplexMessage();
  }
  toString = () => `[${this.name}] ${this.message}`;
  #createComplexMessage = () => {
    return `${this.message}${this.commandId ? ` | 
Command ID: ${this.commandId}` : ""} | 
${this.cause}${this.errorPayload ? ` | 
Error Payload: 
${this.errorPayload}` : ""}`;
  };
};

// src/utils/contextItems.ts
function convertContextItemsToRequestBody(contextItems) {
  return contextItems.map(({ itemId }) => ({ item_id: itemId }));
}

// src/utils/filters.ts
import { r, run } from "@crossingminds/utils";
var FILTER_OPERATORS = {
  equals: "eq",
  doesNotEqual: "neq",
  lessThan: "lt",
  lessThanOrEqual: "lte",
  greaterThan: "gt",
  greaterThanOrEqual: "gte",
  inList: "in",
  notInList: "notin",
  notEmpty: "notempty"
};
function convertFiltersToSearchParamEntries(filters) {
  if (typeof filters === "undefined") {
    return [];
  }
  return filters.map((filter) => {
    const value = run(() => {
      if (!("value" in filter)) {
        return `${filter.itemPropertyName}:${filter.operator}`;
      }
      if (Array.isArray(filter.value)) {
        return `${filter.itemPropertyName}:${filter.operator}:${filter.value.join(",")}`;
      }
      return `${filter.itemPropertyName}:${filter.operator}:${filter.value}`;
    });
    return ["filters", value];
  });
}
function convertFiltersToRequestBody(filters) {
  if (typeof filters === "undefined") {
    return void 0;
  }
  return filters.map(
    ({ itemPropertyName, operator, ...rest }) => r.object(rest, ({ value }) => ({
      property_name: itemPropertyName,
      op: operator,
      ...r.optionalProperties({
        value
      })
    }))
  );
}

// src/utils/dataTypes.ts
var UUID_DATA_TYPE = "uuid";
var BIT_DEPTHS = [8, 16, 32, 64];
var INT = "int";
var signedIntegerDataTypes = BIT_DEPTHS.map(
  (bitDepth) => `${INT}${bitDepth}`
);
var UINT = "uint";
var unsignedIntegerTypes = BIT_DEPTHS.map(
  (bitDepth) => `${UINT}${bitDepth}`
);
var integerDataTypes = [...signedIntegerDataTypes, ...unsignedIntegerTypes];
var FLOAT = "float";
var floatingPointDataTypes = BIT_DEPTHS.map(
  (bitDepth) => `${FLOAT}${bitDepth}`
);
var HEX = "hex";
var hexadecimalDataTypes = BIT_DEPTHS.map(
  (bitDepth) => `${HEX}${bitDepth}`
);
var URL_SAFE = "urlsafe";
var urlSafeDataTypes = BIT_DEPTHS.map(
  (bitDepth) => `${URL_SAFE}${bitDepth}`
);
var BYTES = "bytes";
var rawDataTypes = BIT_DEPTHS.map((bitDepth) => `${BYTES}${bitDepth}`);
var STRING_LENGTHS = new Array(255).fill(0).map((_, i) => i + 1);
var UNICODE = "unicode";
var unicodeDataTypes = STRING_LENGTHS.map(
  (stringLength) => `${UNICODE}${stringLength}`
);
var TEXT = "text";
var IMAGE_URL = "image_url";
var flexibleIdentifiers = [
  UUID_DATA_TYPE,
  ...integerDataTypes,
  ...hexadecimalDataTypes,
  ...urlSafeDataTypes,
  ...rawDataTypes
];
var propertyTypes = [
  INT,
  UINT,
  ...integerDataTypes,
  FLOAT,
  ...floatingPointDataTypes,
  ...rawDataTypes,
  BYTES,
  TEXT,
  IMAGE_URL,
  UNICODE,
  ...unicodeDataTypes
];

// src/utils/interactions.ts
import { dateToUnixTimestamp } from "@crossingminds/utils";
function formatInteractionsForRequestBody(interactions) {
  if (typeof interactions === "undefined") {
    return void 0;
  }
  return interactions.map(({ itemId, interactionType, timestamp }) => ({
    item_id: itemId,
    interaction_type: interactionType,
    timestamp: dateToUnixTimestamp(timestamp ?? /* @__PURE__ */ new Date())
  }));
}

// src/utils/getExpectedExpiration.ts
import { isAccurateDateAvailable } from "@crossingminds/utils";
function getExpectedExpiration() {
  if (!isAccurateDateAvailable) {
    return {};
  }
  return {
    tokenExpectedExpiration: new Date(Date.now() + 29 * 60 * 1e3),
    // 29 minutes
    refreshTokenExpectedExpiration: new Date(
      Date.now() + 29 * 24 * 60 * 60 * 1e3
    )
    // 29 days
  };
}

// src/utils/makeReadOnly.ts
function makeReadOnly(arg) {
  return Object.freeze(arg);
}

// src/utils/maxResults.ts
var convertMaxResultsToSearchParamEntries = (maxResults) => {
  if (typeof maxResults === "undefined") {
    return [];
  }
  return [["amt", maxResults.toString()]];
};

// src/utils/normalizeAllowedRoles.ts
import { run as run2 } from "@crossingminds/utils";

// src/commands/constants.ts
var ACCOUNT_ROLES = [
  "root",
  "manager",
  "backend",
  "frontend",
  "demo_viewer"
];
var ALLOW_ALL_ACCOUNT_ROLES = "*";

// src/errors/APIError.ts
import { isObjectGuard } from "@crossingminds/utils";
var APIError = class extends Error {
  constructor(input, options) {
    super(convertErrorInputToString(input), { cause: options?.cause });
    this.input = input;
    Object.assign(this, input);
  }
  errorCode;
  statusCode;
  errorName;
  errorData;
  description;
  valueOf() {
    return {
      errorCode: this.errorCode,
      errorName: this.errorName,
      errorData: this.errorData,
      statusCode: this.statusCode,
      description: this.description,
      stack: this.stack,
      message: this.message,
      cause: this.cause
    };
  }
  toString() {
    return convertErrorInputToString(this.input);
  }
};
function convertErrorInputToString(input) {
  return `[Crossing Minds API Error]:${format(input.description)}${format(
    input.statusCode
  )}${format(input.errorName)}${format(input.errorCode)}${isObjectGuard(input.errorData) ? "\n" + JSON.stringify(input.errorData, void 0, 2) : ""}`;
}
function format(value) {
  if (typeof value === "undefined") {
    return "";
  }
  return ` 
${value}`;
}
function isAPIErrorGuard(errorCodeOrErrorName, error) {
  if (error instanceof APIError && typeof errorCodeOrErrorName === "number" && error.errorCode === errorCodeOrErrorName) {
    return true;
  }
  if (error instanceof APIError && typeof errorCodeOrErrorName === "string" && error.errorName === errorCodeOrErrorName) {
    return true;
  }
  return false;
}

// src/utils/normalizeAllowedRoles.ts
function normalizeAllowedRoles(allowedAccountRoles) {
  const normalizedRoles = run2(() => {
    if (allowedAccountRoles === ALLOW_ALL_ACCOUNT_ROLES) {
      return [...ACCOUNT_ROLES];
    }
    if (typeof allowedAccountRoles === "string") {
      return [allowedAccountRoles];
    }
    if (Array.isArray(allowedAccountRoles)) {
      return allowedAccountRoles;
    }
    return [];
  });
  const validatedRoles = normalizedRoles.filter(
    (role) => ACCOUNT_ROLES.includes(role)
  );
  if (validatedRoles.length === 0) {
    throw new ClientError({
      message: "Allowed account roles not provided" /* allowedAccountRolesNotProvided */,
      errorPayload: `Received roles "${normalizedRoles.join(
        ", "
      )}". Allowed roles are: "${ACCOUNT_ROLES.join(", ")}".`
    });
  }
  return validatedRoles;
}

// src/utils/normalizeId.ts
import { runtimeTypeChecking as r2 } from "@crossingminds/utils";
function normalizeId(id) {
  return String(r2.required(r2.string(id) || r2.number(id)));
}

// src/utils/normalizeRequestMethods.ts
function normalizeRequestMethods(method) {
  if (method === "patch")
    return "PATCH";
  return method.split(":")[0] ?? method;
}

// src/utils/ratings.ts
function formatRatingsForRequestBody(ratings) {
  if (typeof ratings === "undefined") {
    return void 0;
  }
  return ratings.map(({ itemId, rating }) => ({
    item_id: itemId,
    rating
  }));
}

// src/utils/reranking.ts
import { ObjectTools, run as run3 } from "@crossingminds/utils";
var OPTIONS = {
  activationPeriod: "activation_period",
  defaultMalus: "default_malus"
};
function convertRerankingToSearchParamEntries(rerankings) {
  if (typeof rerankings === "undefined") {
    return [];
  }
  return rerankings.map(({ propertyName, operator, weight, options }) => {
    const optionsSegments = run3(() => {
      if (typeof options === "undefined") {
        return "";
      }
      return ObjectTools.mapToArray(options, ({ key, value }) => {
        if (key in OPTIONS) {
          return `:${OPTIONS[key]}:${value}`;
        }
        return "";
      }).join("");
    });
    return [
      "reranking",
      `${propertyName}:${operator}${weight || options ? ":" : ""}${weight ? weight : ""}${optionsSegments}`
    ];
  });
}
function convertRerankingToRequestBody(rerankings) {
  if (typeof rerankings === "undefined") {
    return void 0;
  }
  return rerankings.map(({ propertyName, operator, weight, options }) => {
    return {
      property_name: propertyName,
      operator,
      weight,
      options: options ? Object.fromEntries(
        Object.entries(options).map(([key, value]) => [
          key in OPTIONS ? OPTIONS[key] : key,
          value
        ])
      ) : void 0
    };
  });
}

// src/utils/scenarios.ts
var RECO_TYPES = [
  "item_to_items",
  "profile_to_items",
  "session_to_items",
  "profile_to_item_properties",
  "profile_to_items_w_ctx_items",
  "session_to_items_w_ctx_items",
  "precomputed_item_to_items",
  "precomputed_profile_to_items",
  "session_to_item_properties",
  "profiles_group_to_items",
  "chat_to_items"
];
var SCENARIO_TYPES = ["case", "ab_test", "condition", "alias"];

// src/utils/typeCheckDatabaseProperty.ts
import { r as r3, isObjectGuard as isObjectGuard2 } from "@crossingminds/utils";
var DATABASE_STATUS = ["pending", "ready", "unknown"];
function typeCheckDatabaseProperty(possibleDatabaseObject) {
  if (isObjectGuard2(possibleDatabaseObject)) {
    try {
      return {
        id: r3.required(r3.string(possibleDatabaseObject.id)),
        ...r3.optionalProperties({
          warnings: r3.array(
            possibleDatabaseObject.warnings,
            (warning) => r3.string(warning)
          ),
          name: r3.string(possibleDatabaseObject.name),
          status: r3.oneOf(possibleDatabaseObject.status, DATABASE_STATUS),
          description: r3.string(possibleDatabaseObject.description),
          itemIdType: r3.string(possibleDatabaseObject.item_id_type),
          userIdType: r3.string(possibleDatabaseObject.user_id_type),
          sessionIdType: r3.string(possibleDatabaseObject.session_id_type)
        })
      };
    } catch {
      return void 0;
    }
  }
  return void 0;
}

// src/utils/typeCheckEvaluatedScenariosValue.ts
import { r as r4 } from "@crossingminds/utils";
var SCENARIO_TYPES2 = ["case", "ab_test", "condition", "alias"];
function typeCheckEvaluatedScenariosValue(evaluated_scenarios) {
  return r4.object(
    evaluated_scenarios,
    (evaluatedScenarios) => r4.optionalProperties({
      runtime: r4.array(
        evaluatedScenarios.runtime,
        (runtime) => r4.required(
          r4.object(runtime, (runtimeObject) => ({
            scenarioType: r4.required(
              r4.oneOf(runtimeObject.scenario_type, SCENARIO_TYPES2)
            ),
            scenarioName: r4.required(r4.string(runtimeObject.scenario_name)),
            to: r4.string(runtimeObject.to)
          }))
        )
      ),
      default: r4.array(
        evaluatedScenarios.default,
        (defaultItem) => r4.required(
          r4.object(defaultItem, (defaultItemObject) => ({
            scenarioType: r4.required(
              r4.oneOf(defaultItemObject.scenario_type, SCENARIO_TYPES2)
            ),
            scenarioName: r4.required(r4.string(defaultItemObject.scenario_name)),
            to: r4.string(defaultItemObject.to)
          }))
        )
      )
    })
  );
}

// src/commands/RenewLoginWithRefreshToken.ts
import { r as r5, run as run4 } from "@crossingminds/utils";
var RENEW_LOGIN_WITH_REFRESH_TOKEN_END_POINT = "login/refresh-token";
var COMMAND_ID = "RenewLoginWithRefreshTokenCommand";
var RenewLoginWithRefreshTokenCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({
    requestUtility,
    setCredentials,
    getCredentials,
    verifyAccountRoleIsAllowed
  }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "post:retryable",
        body: JSON.stringify({
          refresh_token: this.input.refreshToken,
          ...this.input.databaseId ? { db_id: this.input.databaseId } : {}
        }),
        relativeRequestURL: RENEW_LOGIN_WITH_REFRESH_TOKEN_END_POINT
      }
    });
    const output = run4(() => {
      try {
        return r5.object(
          payload,
          ({ org_id, token, refresh_token, role, warnings, database }) => ({
            organizationId: r5.required(r5.string(org_id)),
            token: r5.required(r5.string(token)),
            refreshToken: r5.required(r5.string(refresh_token)),
            role: r5.required(r5.oneOf(role, ACCOUNT_ROLES)),
            ...r5.optionalProperties({
              warnings: r5.array(warnings, r5.string),
              database: typeCheckDatabaseProperty(database)
            })
          })
        );
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        errorPayload: JSON.stringify(payload),
        commandId: COMMAND_ID
      });
    }
    const errorPayload = verifyAccountRoleIsAllowed(output.role);
    if (errorPayload) {
      throw new ClientError({
        message: "Account role not allowed" /* accountRoleNotAllowed */,
        errorPayload,
        commandId: COMMAND_ID
      });
    }
    const { email, serviceLoginId } = getCredentials();
    setCredentials({
      token: output.token,
      refreshToken: output.refreshToken,
      ...getExpectedExpiration(),
      ...serviceLoginId ? { serviceLoginId } : {},
      ...email ? { email } : {},
      ...output?.database ? { currentDatabaseId: output?.database?.id } : {}
    });
    return output;
  };
};

// src/commands/LoginIndividual.ts
import { r as r6, run as run5 } from "@crossingminds/utils";
var LOGIN_INDIVIDUAL_ENDPOINT = "login/individual";
var COMMAND_ID2 = "LoginIndividualCommand";
var LoginIndividualCommand = class {
  constructor(inputParams) {
    this.inputParams = inputParams;
  }
  execute = async ({
    requestUtility,
    setCredentials,
    setPassword,
    verifyAccountRoleIsAllowed
  }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "post:retryable",
        relativeRequestURL: LOGIN_INDIVIDUAL_ENDPOINT,
        body: JSON.stringify(this.inputParams)
      }
    });
    const output = run5(() => {
      try {
        return r6.required(
          r6.object(
            payload,
            ({ warnings, org_id, token, role, refresh_token, database }) => ({
              orgId: r6.required(r6.string(org_id)),
              token: r6.required(r6.string(token)),
              role: r6.required(r6.oneOf(role, ACCOUNT_ROLES)),
              refreshToken: r6.required(r6.string(refresh_token)),
              ...r6.optionalProperties({
                database: typeCheckDatabaseProperty(database),
                warnings: r6.array(warnings, r6.string)
              })
            })
          )
        );
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        errorPayload: JSON.stringify(payload, void 0, 2),
        commandId: COMMAND_ID2
      });
    }
    const errorPayload = verifyAccountRoleIsAllowed(output.role);
    if (errorPayload) {
      throw new ClientError({
        message: "Account role not allowed" /* accountRoleNotAllowed */,
        errorPayload,
        commandId: COMMAND_ID2
      });
    }
    setCredentials({
      email: this.inputParams.email,
      token: output.token,
      refreshToken: output.refreshToken,
      ...getExpectedExpiration(),
      ...r6.optionalProperties({
        currentDatabaseId: output.database?.id
      })
    });
    setPassword(this.inputParams.password);
    return output;
  };
};

// src/commands/LoginService.ts
import { r as r7, run as run6 } from "@crossingminds/utils";
var LOGIN_SERVICE_ENDPOINT = "login/service";
var COMMAND_ID3 = "LoginServiceCommand";
var LoginServiceCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({
    requestUtility,
    setCredentials,
    setPassword,
    verifyAccountRoleIsAllowed
  }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "post:retryable",
        relativeRequestURL: LOGIN_SERVICE_ENDPOINT,
        body: JSON.stringify({
          password: this.input.password,
          db_id: this.input.databaseId,
          name: this.input.serviceLoginId,
          frontend_user_id: this.input.userId,
          frontend_session_id: this.input.sessionId
        })
      }
    });
    const output = run6(() => {
      try {
        return r7.required(
          r7.object(
            payload,
            ({ warnings, org_id, token, role, refresh_token, database }) => ({
              orgId: r7.required(r7.string(org_id)),
              token: r7.required(r7.string(token)),
              role: r7.required(r7.oneOf(role, ACCOUNT_ROLES)),
              refreshToken: r7.required(r7.string(refresh_token)),
              ...r7.optionalProperties({
                database: typeCheckDatabaseProperty(database),
                warnings: r7.array(warnings, r7.string)
              })
            })
          )
        );
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    const errorPayload = verifyAccountRoleIsAllowed(output.role);
    if (errorPayload) {
      throw new ClientError({
        message: "Account role not allowed" /* accountRoleNotAllowed */,
        errorPayload,
        commandId: COMMAND_ID3
      });
    }
    setCredentials({
      serviceLoginId: this.input.serviceLoginId,
      refreshToken: output.refreshToken,
      token: output.token,
      ...r7.optionalProperties({ currentDatabaseId: output?.database?.id }),
      ...getExpectedExpiration()
    });
    setPassword(this.input.password);
    return output;
  };
};

// src/client/sendBeacon.ts
import { joinPath } from "@crossingminds/utils";
function sendBeacon({
  relativeRequestURL,
  endpointBasePath,
  data
}) {
  if (typeof globalThis !== "undefined" && "navigator" in globalThis && "sendBeacon" in globalThis.navigator && typeof globalThis.navigator.sendBeacon === "function") {
    return globalThis.navigator.sendBeacon(
      new URL(
        joinPath([endpointBasePath, relativeRequestURL], {
          trailingSlash: true
        })
      ),
      JSON.stringify(data)
    );
  }
  return false;
}

// src/client/RecommendationClient.ts
var RecommendationClient = class {
  #endpointBasePath = "https://api.crossingminds.com";
  #credentials = {};
  #password;
  /* assertion is ok as error will be thrown in constructor if fetch is not available */
  #fetchClient = void 0;
  /* dev provided callback */
  #onUpdateCredentials;
  /* dev provided callback */
  #onAuthenticationError;
  #allowedAccountRoles;
  // This property is used in a bound function
  #maxErrorRetires = 3;
  // This property is used in a bound function
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  #retryDelay = 250;
  // ms
  constructor({
    allowedAccountRoles,
    initialCredentials,
    fetchClient,
    endpointBasePath,
    onUpdateCredentials,
    onAuthenticationError,
    errorRetryBehavior
  }) {
    const { password, ...credentials } = initialCredentials ?? {};
    this.#allowedAccountRoles = normalizeAllowedRoles(allowedAccountRoles);
    this.#setGlobalFetchClient(fetchClient);
    this.#setPassword(password);
    this.#setCredentials(credentials);
    this.#onAuthenticationError = onAuthenticationError;
    if (typeof errorRetryBehavior?.numberOfRetries === "number") {
      this.#maxErrorRetires = errorRetryBehavior.numberOfRetries;
    }
    if (typeof errorRetryBehavior?.retryDelay === "number") {
      this.#retryDelay = errorRetryBehavior?.retryDelay;
    }
    this.#onUpdateCredentials = onUpdateCredentials;
    if (endpointBasePath) {
      this.#endpointBasePath = endpointBasePath;
    }
    if (initialCredentials) {
      this.#performBackgroundTokenRefresh();
    }
  }
  #sendBeacon = (param) => sendBeacon({ ...param, endpointBasePath: this.#endpointBasePath });
  /** @returns errorPayload */
  #verifyAccountRoleIsAllowed = (role) => {
    if (this.#allowedAccountRoles.includes(role)) {
      return void 0;
    }
    return `Received "${role}" role. Allowed roles for this client instance are: ${this.#allowedAccountRoles.join(
      ", "
    )}.`;
  };
  #backgroundTask = new Promise((resolve) => resolve(void 0));
  #setBackgroundTask = (promise) => {
    this.#backgroundTask = Promise.allSettled([promise]);
  };
  /** Keep credentials up-to-date
   * Will call onAuthenticationError if no credentials are available
   * NOTE: this function must be synchronous. Otherwise `send` can execute first
   */
  #performBackgroundTokenRefresh = () => {
    const {
      currentDatabaseId,
      email,
      serviceLoginId,
      refreshToken,
      refreshTokenExpectedExpiration,
      token,
      tokenExpectedExpiration
    } = this.getCredentials();
    const now = /* @__PURE__ */ new Date();
    if (token && !tokenExpectedExpiration) {
      return;
    }
    if (token && tokenExpectedExpiration && tokenExpectedExpiration > now) {
      return;
    }
    if (refreshToken && !refreshTokenExpectedExpiration || refreshToken && refreshTokenExpectedExpiration && refreshTokenExpectedExpiration > now) {
      const backgroundTask = this.send(
        new RenewLoginWithRefreshTokenCommand({
          refreshToken,
          ...currentDatabaseId ? { databaseId: currentDatabaseId } : {}
        })
        // parent function must be synchronous. Can't use async/await
        // eslint-disable-next-line promise/prefer-await-to-then
      ).catch((error) => {
        this.clearCredentials();
        this.#onAuthenticationError?.(error);
        if (typeof this.#onAuthenticationError !== "function") {
          console.error(error);
        }
        return;
      });
      this.#setBackgroundTask(backgroundTask);
      return;
    }
    if (serviceLoginId && this.#password && currentDatabaseId) {
      const backgroundTask = this.send(
        new LoginServiceCommand({
          serviceLoginId,
          password: this.#password,
          databaseId: currentDatabaseId
        })
      ).catch((error) => {
        this.clearCredentials();
        this.#onAuthenticationError?.(error);
        return;
      });
      this.#setBackgroundTask(backgroundTask);
      return;
    }
    if (email && this.#password) {
      const backgroundTask = this.send(
        new LoginIndividualCommand({
          email,
          password: this.#password,
          ...currentDatabaseId ? { databaseId: currentDatabaseId } : {}
        })
      ).catch((error) => {
        this.clearCredentials();
        this.#onAuthenticationError?.(error);
        return;
      });
      this.#setBackgroundTask(backgroundTask);
      return;
    }
    if (!(tokenExpectedExpiration instanceof Date && tokenExpectedExpiration < now && refreshTokenExpectedExpiration instanceof Date && refreshTokenExpectedExpiration < now) && token) {
      return;
    }
    this.clearCredentials();
    this.#onAuthenticationError?.(
      new ClientError({ message: "No credentials present" /* noCredentialsPresent */ })
    );
    return;
  };
  #setCredentials = (nextCredentials) => {
    this.#credentials = makeReadOnly(nextCredentials);
    this.#onUpdateCredentials?.(this.#credentials);
  };
  clearCredentials = () => {
    this.#setCredentials({});
  };
  getCredentials = () => this.#credentials;
  #setPassword = (nextPassword) => {
    this.#password = nextPassword;
  };
  #requestUtility = async ({ requestOptions }) => {
    const request = () => this.#apiRequest(requestOptions);
    let attempts = 0;
    const hasAttemptsRemaining = () => {
      return attempts <= this.#maxErrorRetires;
    };
    const iterateAttempts = () => {
      attempts++;
    };
    const manageRetiresAndErrors = async () => {
      const initialRequestCredentials = this.getCredentials();
      const response = await run7(async () => {
        try {
          iterateAttempts();
          return await request();
        } catch (error) {
          console.warn("RECOMMENDATION CLIENT FETCH ERROR");
          throw error;
        }
      });
      if (response.status == 204) {
        return void 0;
      }
      if (response.status == 202) {
        return {
          warnings: ["The request was accepted but not yet processed."]
        };
      }
      const payload = await run7(async () => {
        try {
          return await response.json();
        } catch (error) {
          return void 0;
        }
      });
      if (response.status == 200 || response.status == 201) {
        return payload;
      }
      if (isObjectGuard3(payload) && isNumberGuard(payload.error_code) && isStringGuard(payload.error_name)) {
        const errorFromServer = new APIError({
          statusCode: response.status,
          errorCode: payload.error_code,
          errorName: payload.error_name,
          errorData: isObjectGuard3(payload.error_data) ? payload.error_data : void 0,
          description: isStringGuard(payload.message) ? payload.message : void 0
        });
        if (isAPIErrorGuard(22, errorFromServer)) {
          await this.#backgroundTask;
          if (initialRequestCredentials.token === this.getCredentials().token) {
            const {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
              token,
              // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
              tokenExpectedExpiration,
              ...restCredentials
            } = this.getCredentials();
            this.#setCredentials(restCredentials);
            this.#performBackgroundTokenRefresh();
            await this.#backgroundTask;
            if (this.getCredentials().token && hasAttemptsRemaining()) {
              return manageRetiresAndErrors();
            }
          } else {
            if (this.getCredentials().token && hasAttemptsRemaining()) {
              return manageRetiresAndErrors();
            }
          }
          throw errorFromServer;
        }
        const retryableMethods = [
          "get",
          "post:retryable",
          "post:keepalive",
          "delete"
        ];
        const retryableStatusCodes = [
          500,
          502
        ];
        if (isItemInArrayGuard(response.status, retryableStatusCodes) && isItemInArrayGuard(requestOptions.method, retryableMethods) && hasAttemptsRemaining()) {
          return manageRetiresAndErrors();
        }
        throw errorFromServer;
      }
      throw new APIError({
        statusCode: response.status,
        errorName: "UnknownError",
        errorData: payload
      });
    };
    return await manageRetiresAndErrors();
  };
  #apiRequest = ({
    relativeRequestURL,
    method,
    body,
    searchParams
  }) => {
    const url = new URL(
      joinPath2([this.#endpointBasePath, relativeRequestURL], {
        trailingSlash: true
      })
    );
    if (Array.isArray(searchParams)) {
      searchParams.forEach(([key, value]) => {
        if (typeof value === "string") {
          url.searchParams.append(key, value);
        } else if (Array.isArray(value)) {
          value.forEach((item) => {
            if (typeof item === "string") {
              url.searchParams.append(key, item);
            }
          });
        }
      });
    } else if (isObjectGuard3(searchParams)) {
      Object.entries(searchParams).forEach(([key, value]) => {
        if (typeof value !== "undefined") {
          url.searchParams.set(key, value);
        }
      });
    }
    return this.#fetchClient(url.toString(), {
      method: normalizeRequestMethods(method),
      ...body ? { body } : {},
      ...method === "post:keepalive" ? { keepalive: true } : {},
      headers: {
        "Content-Type": "application/json",
        ...this.#credentials.token ? { Authorization: `Bearer ${this.#credentials.token}` } : {}
      }
    });
  };
  #setGlobalFetchClient = (devProvidedFetchClient) => {
    if (devProvidedFetchClient !== void 0) {
      if (typeof devProvidedFetchClient === "function") {
        this.#fetchClient = devProvidedFetchClient;
      } else {
        throw new ClientError({ message: "No fetch client available" /* noFetchClient */ });
      }
    } else {
      if (typeof globalThis.fetch === "function") {
        this.#fetchClient = globalThis.fetch.bind(globalThis);
      } else {
        throw new ClientError({ message: "No fetch client available" /* noFetchClient */ });
      }
    }
  };
  send = async (commandInstance) => {
    await this.#backgroundTask;
    return commandInstance.execute({
      requestUtility: this.#requestUtility,
      getCredentials: this.getCredentials,
      setCredentials: this.#setCredentials,
      setPassword: this.#setPassword,
      sendBeacon: this.#sendBeacon,
      verifyAccountRoleIsAllowed: this.#verifyAccountRoleIsAllowed
    });
  };
};

// src/client/localStorage/persistCredentialsInLocalStorage.ts
import { isObjectGuard as isObjectGuard4, isStringGuard as isStringGuard2, r as r8, run as run8 } from "@crossingminds/utils";
function persistCredentialsInLocalStorage({
  localStorageKey
}) {
  const localStorageAccess = run8(() => {
    if (typeof globalThis !== "undefined" && isObjectGuard4(globalThis.localStorage) && typeof globalThis.localStorage.getItem === "function") {
      return globalThis.localStorage;
    }
    throw new ClientError({
      message: "Local Storage not available" /* localStorageNotAvailable */
    });
  });
  const rawLocalCredentials = localStorageAccess.getItem(localStorageKey);
  const localCredentials = sanitizeCredentials(rawLocalCredentials);
  if (!Object.keys(localCredentials ?? {}).length) {
    localStorageAccess.removeItem(localStorageKey);
  }
  const handleUpdateCredentials = (nextCredentials) => {
    localStorageAccess.setItem(localStorageKey, JSON.stringify(nextCredentials));
  };
  return {
    onUpdateCredentials: handleUpdateCredentials,
    ...localCredentials ? { initialCredentials: localCredentials } : {}
  };
}
function sanitizeCredentials(unsanitaryCredentials) {
  try {
    if (isStringGuard2(unsanitaryCredentials)) {
      const credentials = JSON.parse(unsanitaryCredentials);
      const output = r8.object(
        credentials,
        ({
          currentDatabaseId,
          serviceLoginId,
          email,
          refreshToken,
          refreshTokenExpectedExpiration,
          token,
          tokenExpectedExpiration
        }) => r8.optionalProperties({
          currentDatabaseId: r8.string(currentDatabaseId),
          serviceLoginId: r8.string(serviceLoginId),
          email: r8.string(email),
          refreshToken: r8.string(refreshToken),
          token: r8.string(token),
          tokenExpectedExpiration: r8.date(tokenExpectedExpiration),
          refreshTokenExpectedExpiration: r8.date(
            refreshTokenExpectedExpiration
          )
        })
      );
      return output;
    }
  } catch {
    return void 0;
  }
  return void 0;
}

// src/CredentialStore/shouldUpgradeVersion.ts
function shouldUpgradeVersion(a, b) {
  const [aMajor, aMinor, aPatch] = splitVersion(a);
  const [bMajor, bMinor, bPatch] = splitVersion(b);
  if (aMajor !== bMajor) {
    return aMajor < bMajor;
  }
  if (aMinor !== bMinor) {
    return aMinor < bMinor;
  }
  if (aPatch !== bPatch) {
    return aPatch < bPatch;
  }
  return false;
}
function splitVersion(version) {
  const split = version.split(".");
  if (split.length < 3) {
    for (let i = split.length; i < 3; i++) {
      split.push("0");
    }
  }
  const numbers = split.map((i) => parseInt(i) ?? 0);
  return numbers;
}

// src/CredentialStore/createObjectHash.ts
import { isObjectGuard as isObjectGuard5 } from "@crossingminds/utils";
import md5 from "md5";
function createObjectHash(inputObject) {
  const flattenedObject = flattenSerializableObject(inputObject);
  flattenedObject.sort();
  return md5(flattenSerializableObject(inputObject).join(","));
}
function flattenSerializableObject(credentialObject, prefix = "") {
  const input = JSON.parse(JSON.stringify(credentialObject));
  let result = [];
  for (const key in input) {
    const value = input[key];
    if (isObjectGuard5(value) || Array.isArray(value)) {
      result = [
        ...result,
        ...flattenSerializableObject(value, prefix + key + ".")
      ];
    } else {
      result.push(prefix + key + ":" + value);
    }
  }
  result.sort();
  return result;
}

// src/CredentialStore/LocalStorageManagement.ts
import { isArrayGuard, isObjectGuard as isObjectGuard6, r as r9, run as run9 } from "@crossingminds/utils";

// src/CredentialStore/constants.ts
import md52 from "md5";
var CREDENTIAL_STORE_VERSION = "0.0.0";
var [majorVersion] = CREDENTIAL_STORE_VERSION.split(".");
var CREDENTIAL_STORE_VERSION_LOCAL_STORAGE_KEY = md52(
  `crossing-minds-credential-store-${majorVersion}`
);
var hasLocalStorage = typeof window === "object" && typeof window.localStorage === "object" && typeof localStorage === "object";
function isCredentialObjectGuard(storeValue) {
  if (typeof storeValue === "undefined") {
    return false;
  }
  return "token" in storeValue;
}
function isMutexObjectGuard(storeValue) {
  if (typeof storeValue === "undefined") {
    return false;
  }
  return "promise" in storeValue;
}

// src/CredentialStore/LocalStorageManagement.ts
var StoreLocalStorageManagement = class {
  version = CREDENTIAL_STORE_VERSION;
  store = /* @__PURE__ */ new Map();
  /** Several versions of the CredentialStore may exist in an app.
   * If the global CredentialStore is replaced with a newer version, the original is considered "outdated". */
  #isOutdated = false;
  /** Get store entries from outdated store. This store will be set as "outdated" */
  transferOutdatedStore = () => {
    this.#isOutdated = true;
    return Array.from(this.store.entries());
  };
  setPersistentStoreEntries = () => {
    if (hasLocalStorage && !this.#isOutdated) {
      const entries = [];
      this.store.forEach((value, credentialHash) => {
        if (isCredentialObjectGuard(value)) {
          entries.push([credentialHash, value]);
        }
      });
      localStorage.setItem(
        CREDENTIAL_STORE_VERSION_LOCAL_STORAGE_KEY,
        JSON.stringify(entries)
      );
    }
  };
  getPersistentStoreEntries = () => {
    if (hasLocalStorage) {
      const rawPersistedStore = localStorage.getItem(CREDENTIAL_STORE_VERSION_LOCAL_STORAGE_KEY) ?? void 0;
      const persistedStore = run9(() => {
        try {
          return r9.required(
            r9.array(JSON.parse(rawPersistedStore ?? ""), (entry) => {
              if (isArrayGuard(entry)) {
                const [credentialHash, credentialObject] = entry;
                return [
                  r9.required(r9.string(credentialHash)),
                  r9.required(
                    r9.object(
                      credentialObject,
                      ({
                        token,
                        clientOptions,
                        databaseId,
                        refreshToken,
                        refreshTokenExpectedExpiration,
                        serviceLoginId,
                        sessionId,
                        tokenExpectedExpiration,
                        userId
                      }) => ({
                        token: r9.required(r9.string(token)),
                        databaseId: r9.required(r9.string(databaseId)),
                        serviceLoginId: r9.required(r9.string(serviceLoginId)),
                        ...r9.optionalProperties({
                          clientOptions: isObjectGuard6(clientOptions) ? (
                            // Leaving this object weakly typed as all properties are optional.
                            clientOptions
                          ) : void 0,
                          refreshToken: r9.string(refreshToken),
                          refreshTokenExpectedExpiration: r9.date(
                            refreshTokenExpectedExpiration
                          ),
                          sessionId: r9.string(sessionId),
                          tokenExpectedExpiration: r9.date(
                            tokenExpectedExpiration
                          ),
                          userId: r9.string(userId)
                        })
                      })
                    )
                  )
                ];
              }
              return void 0;
            })
          );
        } catch {
          console.warn("[CredentialStore] Failed to parse persisted store.");
          return void 0;
        }
      });
      if (typeof persistedStore === "undefined") {
        return void 0;
      }
      return persistedStore.filter(
        ({ 1: credentialObject }) => credentialObject.tokenExpectedExpiration && credentialObject.tokenExpectedExpiration > /* @__PURE__ */ new Date()
      );
    }
    return void 0;
  };
};

// src/CredentialStore/StoreManagement.ts
var STORE_SIZE_LIMIT = 100;
var StoreManagement = class extends StoreLocalStorageManagement {
  getStoreValue = (credentialHash) => {
    return this.store.get(credentialHash);
  };
  replaceStore = (storeEntries) => {
    this.store = new Map(storeEntries);
  };
  removeStoreValue = (credentialHash) => {
    this.store.delete(credentialHash);
    this.setPersistentStoreEntries();
  };
  setStoreValue = (credentialHash, storeValue) => {
    this.store.delete(credentialHash);
    this.store.set(credentialHash, storeValue);
    if (this.store.size > STORE_SIZE_LIMIT) {
      const countOverLimit = this.store.size - STORE_SIZE_LIMIT;
      const entriesIterable = this.store.entries();
      let deletedCount = 0;
      let loops = 0;
      while (deletedCount < countOverLimit && loops < this.store.size) {
        loops++;
        const [credentialHashToRemove, value] = entriesIterable.next().value;
        if (isCredentialObjectGuard(value)) {
          this.store.delete(credentialHashToRemove);
          deletedCount++;
        }
      }
    }
    this.setPersistentStoreEntries();
  };
};

// src/CredentialStore/InternalClientManagement.ts
var InternalClientManagement = class extends StoreManagement {
  clients = /* @__PURE__ */ new Map();
  getClient = (credentialHash) => {
    return this.clients.get(credentialHash);
  };
  createNewAuthenticatedClientFromStore = (credentialHash) => {
    const storeValue = this.getStoreValue(credentialHash);
    if (typeof storeValue === "undefined" || !isCredentialObjectGuard(storeValue)) {
      console.error(
        "[Credential Store] Attempted to `createNewAuthenticatedClientFromStore` when Store did not contain valid credentials.",
        credentialHash,
        storeValue
      );
      this.removeStoreValue(credentialHash);
      return;
    }
    const client = new RecommendationClient({
      ...storeValue?.clientOptions ?? {},
      allowedAccountRoles: storeValue?.clientOptions?.allowedAccountRoles ?? "frontend",
      initialCredentials: {
        currentDatabaseId: storeValue.databaseId,
        refreshToken: storeValue.refreshToken,
        refreshTokenExpectedExpiration: storeValue.refreshTokenExpectedExpiration,
        token: storeValue.token,
        tokenExpectedExpiration: storeValue.tokenExpectedExpiration,
        serviceLoginId: storeValue.serviceLoginId
      },
      onUpdateCredentials: (newCredentials) => {
        if (!newCredentials.token) {
          this.removeStoreValue(credentialHash);
          return;
        }
        this.setStoreValue(credentialHash, {
          ...storeValue,
          token: newCredentials.token,
          refreshToken: newCredentials.refreshToken,
          refreshTokenExpectedExpiration: newCredentials.refreshTokenExpectedExpiration,
          tokenExpectedExpiration: newCredentials.tokenExpectedExpiration
        });
      }
    });
    this.clients.set(credentialHash, client);
  };
  createClientsForOpenNetworkCalls = (nextStoreEntries) => {
    nextStoreEntries.forEach(async ([credentialHash, storeValue]) => {
      if (isMutexObjectGuard(storeValue)) {
        const { token, refreshToken } = await storeValue.promise;
        const nextStoreValue = {
          token,
          refreshToken,
          databaseId: storeValue.databaseId,
          serviceLoginId: storeValue.serviceLoginId,
          clientOptions: storeValue.clientOptions,
          ...getExpectedExpiration()
        };
        this.setStoreValue(credentialHash, nextStoreValue);
        this.createNewAuthenticatedClientFromStore(credentialHash);
      }
    });
  };
};

// src/CredentialStore/PublicClientManagement.ts
var PublicClientManagement = class extends InternalClientManagement {
  getAuthenticatedClient = async (input) => {
    const credentialHash = createObjectHash(input);
    const existingClient = this.getClient(credentialHash);
    if (existingClient) {
      return existingClient;
    }
    const existingStoreValue = this.getStoreValue(credentialHash);
    if (isCredentialObjectGuard(existingStoreValue)) {
      this.createNewAuthenticatedClientFromStore(credentialHash);
      const newClient = this.getClient(credentialHash);
      if (!newClient) {
        throw new Error(
          "[CredentialStore] Failed to create new client from store."
        );
      }
      return newClient;
    }
    if (isMutexObjectGuard(existingStoreValue)) {
      await existingStoreValue.promise;
      const newClient = this.getClient(credentialHash);
      if (!newClient) {
        throw new Error(
          "[CredentialStore] Failed to get client after mutex resolved."
        );
      }
      return newClient;
    }
    const mutex = crypto.randomUUID();
    const temporaryClient = new RecommendationClient({
      ...input.clientOptions ?? {},
      allowedAccountRoles: input.clientOptions?.allowedAccountRoles || "frontend"
    });
    try {
      const authPromise = temporaryClient.send(
        new LoginServiceCommand({
          password: input.password,
          serviceLoginId: input.serviceLoginId,
          databaseId: input.databaseId,
          sessionId: input.sessionId,
          // TODO: (Phase 2) use sessionId from internally managed state
          userId: input.userId
        })
      );
      const { password, ...restInput } = input;
      const mutexObject = {
        ...restInput,
        mutex,
        promise: authPromise,
        credentialHash
      };
      this.setStoreValue(credentialHash, mutexObject);
      await authPromise;
      const { token, ...restAuth } = temporaryClient.getCredentials();
      if (!token) {
        throw new Error("[CredentialStore] Failed to authenticate");
      }
      this.setStoreValue(credentialHash, {
        ...restAuth,
        token,
        clientOptions: input.clientOptions,
        databaseId: input.databaseId,
        serviceLoginId: input.serviceLoginId,
        userId: input.userId,
        sessionId: input.sessionId
      });
      this.createNewAuthenticatedClientFromStore(credentialHash);
      const newClient = this.getClient(credentialHash);
      if (!newClient) {
        throw new Error(
          "[CredentialStore] Failed to create new client from fresh authentication."
        );
      }
      return newClient;
    } catch {
      this.removeStoreValue(credentialHash);
      throw new Error("[CredentialStore] Client could not be created");
    }
  };
};

// src/CredentialStore/CredentialStore.ts
var CredentialStore = class extends PublicClientManagement {
  constructor() {
    super();
    const existingStore = this.getGlobalStore();
    if (existingStore) {
      const isUpgradeNeeded = shouldUpgradeVersion(
        existingStore.version,
        this.version
      );
      if (!isUpgradeNeeded) {
        return existingStore;
      } else {
        console.info(
          `[CredentialStore] Upgrading global store from ${existingStore.version} to ${this.version}`
        );
        const migratingStoreEntries = existingStore.transferOutdatedStore();
        this.createClientsForOpenNetworkCalls(migratingStoreEntries);
        this.replaceStore(migratingStoreEntries);
      }
    } else {
      const persistedStoreEntries = this.getPersistentStoreEntries();
      if (persistedStoreEntries) {
        this.replaceStore(persistedStoreEntries);
      }
    }
    this.setGlobalStore();
    return this;
  }
  getGlobalStore = () => {
    return globalThis._credentialStore;
  };
  setGlobalStore = () => {
    globalThis._credentialStore = this;
  };
};

// src/commands/CreateInteractionsForManyUsers.ts
import { dateToUnixTimestamp as dateToUnixTimestamp2, r as r10 } from "@crossingminds/utils";
var CREATE_INTERACTIONS_FOR_MANY_USERS_URL = "interactions-bulk";
var CreateInteractionsForManyUsersCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        relativeRequestURL: CREATE_INTERACTIONS_FOR_MANY_USERS_URL,
        method: "post:keepalive",
        body: JSON.stringify({
          interactions: this.input.interactions.map(
            ({
              userId,
              itemId,
              interactionType,
              timestamp,
              properties
            }) => ({
              user_id: userId,
              item_id: itemId,
              interaction_type: interactionType,
              timestamp: timestamp instanceof Date ? dateToUnixTimestamp2(timestamp) : void 0,
              properties
            })
          )
        })
      }
    });
    const output = r10.object(
      payload,
      ({ warnings }) => r10.optionalProperties({
        warnings: r10.array(warnings, r10.string)
      })
    );
    return output;
  };
};

// src/commands/CreateIndividualAccount.ts
import { r as r11, run as run10 } from "@crossingminds/utils";
var CREATE_INDIVIDUAL_ACCOUNT_URL = "accounts/individual";
var COMMAND_ID4 = "CreateIndividualAccountCommand";
var CreateIndividualAccountCommand = class {
  constructor(input) {
    this.input = input;
  }
  async execute({ requestUtility }) {
    const payload = await requestUtility({
      requestOptions: {
        method: "post",
        relativeRequestURL: CREATE_INDIVIDUAL_ACCOUNT_URL,
        body: JSON.stringify({
          email: this.input.email,
          role: this.input.role,
          email_redirect_url: this.input.emailRedirectUrl,
          first_name: this.input.firstName,
          last_name: this.input.lastName,
          metadata: this.input.metadata,
          password: this.input.password
        })
      }
    });
    const output = run10(() => {
      try {
        return r11.required(
          r11.object(payload, ({ warnings, id }) => ({
            ...r11.optionalProperties({
              warnings: r11.array(warnings, r11.string)
            }),
            id: r11.required(r11.string(id))
          }))
        );
      } catch {
        return void 0;
      }
    });
    if (!output) {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: COMMAND_ID4,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  }
};

// src/commands/CreateOneAnonymousSessionInteraction.ts
import {
  dateToUnixTimestamp as dateToUnixTimestamp3,
  r as r12,
  secondsToMilliseconds
} from "@crossingminds/utils";
var createOneAnonymousSessionInteractionEndpoint = ({
  sessionId,
  itemId
}) => `sessions/${sessionId}/items/${itemId}/interactions/`;
var createSessionInteractionsForBeaconEndpoint = ({
  sessionId
}) => `beacon/sessions/${sessionId}/interactions-bulk`;
var CreateOneAnonymousSessionInteractionCommand = class {
  constructor(input) {
    this.input = input;
    if (typeof input.networkInterface === "undefined") {
      this.input.networkInterface = "sendBeacon";
    }
  }
  execute = async ({
    requestUtility,
    getCredentials,
    sendBeacon: sendBeacon2
  }) => {
    const { token, tokenExpectedExpiration } = getCredentials();
    if (this.input.networkInterface === "sendBeacon" && /* Must have valid token or beacon will fail */
    token && tokenExpectedExpiration && tokenExpectedExpiration.getTime() > Date.now() + secondsToMilliseconds(1)) {
      const beaconResponse = sendBeacon2({
        relativeRequestURL: createSessionInteractionsForBeaconEndpoint({
          sessionId: this.input.sessionId
        }),
        data: {
          token,
          interactions: [
            {
              item_id: this.input.itemId,
              interaction_type: this.input.interactionType,
              timestamp: this.input.timestamp instanceof Date ? dateToUnixTimestamp3(this.input.timestamp) : void 0,
              properties: this.input.properties
            }
          ]
        }
      });
      if (beaconResponse) {
        return true;
      }
    }
    const payload = await requestUtility({
      requestOptions: {
        relativeRequestURL: createOneAnonymousSessionInteractionEndpoint({
          sessionId: this.input.sessionId,
          itemId: this.input.itemId
        }),
        method: "post:keepalive",
        body: JSON.stringify({
          interaction_type: this.input.interactionType,
          timestamp: this.input.timestamp ? dateToUnixTimestamp3(this.input.timestamp) : void 0,
          properties: this.input.properties
        })
      }
    });
    const output = r12.object(
      payload,
      ({ warnings }) => r12.optionalProperties({
        warnings: r12.array(warnings, r12.string)
      })
    );
    return output;
  };
};

// src/commands/CreateOneUserInteraction.ts
import {
  dateToUnixTimestamp as dateToUnixTimestamp4,
  r as r13,
  secondsToMilliseconds as secondsToMilliseconds2
} from "@crossingminds/utils";
var createOneUserInteractionEndpoint = ({
  itemId,
  userId
}) => `users/${userId}/interactions/${itemId}`;
var createInteractionsForAUserInBulkForBeacon = ({
  userId
}) => `beacon/users/${userId}/interactions-bulk`;
var CreateOneUserInteractionCommand = class {
  constructor(input) {
    this.input = input;
    if (typeof input.networkInterface === "undefined") {
      this.input.networkInterface = "sendBeacon";
    }
  }
  execute = async ({
    requestUtility,
    getCredentials,
    sendBeacon: sendBeacon2
  }) => {
    const { token, tokenExpectedExpiration } = getCredentials();
    if (this.input.networkInterface === "sendBeacon" && /* Must have valid token or beacon will fail */
    token && tokenExpectedExpiration && tokenExpectedExpiration.getTime() > Date.now() + secondsToMilliseconds2(1)) {
      const beaconResponse = sendBeacon2({
        relativeRequestURL: createInteractionsForAUserInBulkForBeacon({
          userId: this.input.userId
        }),
        data: {
          token,
          interactions: [
            {
              item_id: this.input.itemId,
              interaction_type: this.input.interactionType,
              timestamp: this.input.timestamp instanceof Date ? dateToUnixTimestamp4(this.input.timestamp) : void 0,
              properties: this.input.properties
            }
          ]
        }
      });
      if (beaconResponse) {
        return true;
      }
    }
    const payload = await requestUtility({
      requestOptions: {
        relativeRequestURL: createOneUserInteractionEndpoint({
          itemId: this.input.itemId,
          userId: this.input.userId
        }),
        method: "post:keepalive",
        body: JSON.stringify({
          interaction_type: this.input.interactionType,
          properties: this.input.properties,
          ...this.input.timestamp ? { timestamp: dateToUnixTimestamp4(this.input.timestamp) } : {}
        })
      }
    });
    const output = r13.object(
      payload,
      ({ warnings }) => r13.optionalProperties({
        warnings: r13.array(warnings, r13.string)
      })
    );
    return output;
  };
};

// src/commands/CreateServiceAccount.ts
import { r as r14, run as run11 } from "@crossingminds/utils";
var CREATE_SERVICE_ACCOUNT_URL = "accounts/service";
var COMMAND_ID5 = "CreateServiceAccountCommand";
var CreateServiceAccountCommand = class {
  constructor(input) {
    this.input = input;
  }
  async execute({ requestUtility }) {
    const payload = await requestUtility({
      requestOptions: {
        method: "post",
        relativeRequestURL: CREATE_SERVICE_ACCOUNT_URL,
        body: JSON.stringify({
          name: this.input.name,
          role: this.input.role,
          metadata: this.input.metadata,
          password: this.input.password
        })
      }
    });
    const output = run11(() => {
      try {
        return r14.required(
          r14.object(payload, ({ warnings, id }) => ({
            ...r14.optionalProperties({
              warnings: r14.array(warnings, r14.string)
            }),
            id: r14.required(r14.string(id))
          }))
        );
      } catch {
        return void 0;
      }
    });
    if (!output) {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: COMMAND_ID5,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  }
};

// src/commands/CreateServiceAccountKey.ts
import {
  dateToUnixTimestamp as dateToUnixTimestamp5,
  parseUnixTimestamp,
  r as r15,
  run as run12
} from "@crossingminds/utils";
var CREATE_SERVICE_ACCOUNT_KEY_URL = "accounts/service/keys";
var COMMAND_ID6 = "CreateServiceAccountKeyCommand";
var CreateServiceAccountKeyCommand = class {
  constructor(input) {
    this.input = input;
  }
  async execute({ requestUtility }) {
    const payload = await requestUtility({
      requestOptions: {
        method: "post",
        relativeRequestURL: CREATE_SERVICE_ACCOUNT_KEY_URL,
        body: JSON.stringify({
          name: this.input.name,
          expiration_timestamp: this.input.expirationTimestamp instanceof Date ? dateToUnixTimestamp5(this.input.expirationTimestamp) : void 0,
          metadata: this.input.metadata
        })
      }
    });
    const output = run12(() => {
      try {
        return r15.required(
          r15.object(
            payload,
            ({
              warnings,
              id,
              created_timestamp,
              expiration_timestamp,
              is_expired,
              metadata,
              key
            }) => ({
              id: r15.required(r15.string(id)),
              createdTimestamp: r15.required(
                parseUnixTimestamp(r15.number(created_timestamp))
              ),
              expirationTimestamp: parseUnixTimestamp(
                r15.number(expiration_timestamp)
              ),
              isExpired: r15.required(r15.boolean(is_expired)),
              metadata: r15.required(
                r15.object(metadata, (metadataObj) => metadataObj)
              ),
              key: r15.required(r15.string(key)),
              ...r15.optionalProperties({
                warnings: r15.array(warnings, r15.string)
              })
            })
          )
        );
      } catch {
        return void 0;
      }
    });
    if (!output) {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: COMMAND_ID6,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  }
};

// src/commands/DeleteIndividualAccount.ts
var DELETE_INDIVIDUAL_ACCOUNT_URL = "accounts/individual/";
var DeleteIndividualAccountCommand = class {
  constructor(input) {
    this.input = input;
  }
  async execute({ requestUtility }) {
    await requestUtility({
      requestOptions: {
        method: "delete",
        relativeRequestURL: DELETE_INDIVIDUAL_ACCOUNT_URL,
        body: JSON.stringify({
          email: this.input.email
        })
      }
    });
  }
};

// src/commands/DeleteServiceAccount.ts
var DELETE_SERVICE_ACCOUNT_URL = "accounts/service/";
var DeleteServiceAccountCommand = class {
  constructor(input) {
    this.input = input;
  }
  async execute({ requestUtility }) {
    await requestUtility({
      requestOptions: {
        method: "delete",
        relativeRequestURL: DELETE_SERVICE_ACCOUNT_URL,
        body: JSON.stringify({
          name: this.input.name
        })
      }
    });
  }
};

// src/commands/DeleteServiceAccountKey.ts
var getDeleteServiceAccountKeyEndpoint = (keyId) => `accounts/service/keys/${keyId}/`;
var DeleteServiceAccountKeyCommand = class {
  constructor(input) {
    this.input = input;
  }
  async execute({ requestUtility }) {
    await requestUtility({
      requestOptions: {
        method: "delete",
        relativeRequestURL: getDeleteServiceAccountKeyEndpoint(this.input.keyId)
      }
    });
  }
};

// src/commands/GetAllScenarios.ts
import { r as r16, run as run13 } from "@crossingminds/utils";
var GET_ALL_SCENARIOS_URL = "scenarios";
var COMMAND_ID7 = "GetAllScenarios";
var GetAllScenariosCommand = class {
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "get",
        relativeRequestURL: GET_ALL_SCENARIOS_URL
      }
    });
    const output = run13(() => {
      try {
        return r16.required(
          r16.object(payload, ({ scenarios, warnings }) => ({
            scenarios: r16.required(
              r16.array(
                scenarios,
                (scenarioObj) => r16.object(
                  scenarioObj,
                  ({
                    warnings: scenarioWarnings,
                    reco_type,
                    name: scenarioName,
                    scenario
                  }) => ({
                    recoType: r16.required(r16.oneOf(reco_type, RECO_TYPES)),
                    scenarioName: r16.required(r16.string(scenarioName)),
                    scenario: r16.required(
                      r16.object(
                        scenario,
                        ({
                          scenario_type,
                          metadata,
                          case: scenarioCase,
                          alias,
                          condition,
                          ab_test
                        }) => ({
                          scenarioType: r16.required(
                            r16.oneOf(scenario_type, SCENARIO_TYPES)
                          ),
                          metadata: r16.object(
                            metadata,
                            ({ ...metadataProperties }) => metadataProperties
                          ),
                          case: r16.object(
                            scenarioCase,
                            ({ ...caseProperties }) => caseProperties
                          ),
                          alias: r16.object(alias, ({ scenario_name }) => ({
                            scenarioName: r16.string(scenario_name)
                          })),
                          condition: r16.object(
                            condition,
                            ({ ...conditionProperties }) => ({
                              conditionType: r16.string(
                                conditionProperties.condition_type
                              ),
                              if: r16.object(
                                conditionProperties.if,
                                ({ ...ifProperties }) => ({
                                  ...ifProperties
                                })
                              ),
                              then: r16.string(conditionProperties.then),
                              else: r16.string(conditionProperties.else)
                            })
                          ),
                          abTest: r16.object(
                            ab_test,
                            ({ id, scenario_a, scenario_b }) => ({
                              id: r16.string(id),
                              scenarioA: r16.string(scenario_a),
                              scenarioB: r16.string(scenario_b)
                            })
                          )
                        })
                      )
                    ),
                    ...r16.optionalProperties({
                      warnings: r16.array(scenarioWarnings, r16.string)
                    })
                  })
                )
              )
            ),
            ...r16.optionalProperties({
              warnings: r16.array(warnings, r16.string)
            })
          }))
        );
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: COMMAND_ID7,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  };
};

// src/commands/GetCurrentDatabase.ts
import { r as r17, run as run14 } from "@crossingminds/utils";
var GET_CURRENT_DATABASE_ENDPOINT = "databases/current";
var COMMAND_NAME = "GetCurrentDatabaseCommand";
var GetCurrentDatabaseCommand = class {
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        relativeRequestURL: GET_CURRENT_DATABASE_ENDPOINT,
        method: "get"
      }
    });
    const output = run14(() => {
      try {
        return r17.required(
          r17.object(
            payload,
            ({
              status: dbStatus,
              id,
              name: dbName,
              description,
              item_id_type,
              user_id_type,
              session_id_type,
              org_id,
              counters,
              core_config,
              metadata,
              warnings
            }) => ({
              status: r17.required(r17.oneOf(dbStatus, DATABASE_STATUS)),
              id: r17.required(r17.string(id)),
              name: r17.required(r17.string(dbName)),
              description: r17.required(r17.string(description)),
              itemIdType: r17.required(
                r17.oneOf(item_id_type, flexibleIdentifiers)
              ),
              userIdType: r17.required(
                r17.oneOf(user_id_type, flexibleIdentifiers)
              ),
              sessionIdType: r17.required(
                r17.oneOf(session_id_type, flexibleIdentifiers)
              ),
              orgId: r17.required(r17.string(org_id)),
              counters: r17.required(
                r17.object(counters, (countersObj) => {
                  return {
                    rating: r17.required(r17.number(countersObj.rating)),
                    user: r17.required(r17.number(countersObj.user)),
                    item: r17.required(r17.number(countersObj.item)),
                    ...r17.optionalProperties({
                      warnings: r17.array(
                        countersObj.warnings,
                        (warning) => r17.string(warning)
                      )
                    })
                  };
                })
              ),
              coreConfig: r17.required(
                r17.object(core_config, (coreConfig) => coreConfig)
              ),
              metadata: r17.required(
                r17.object(metadata, (metadataObj) => metadataObj)
              ),
              ...r17.optionalProperties({
                warnings: r17.array(warnings, (warning) => r17.string(warning))
              })
            })
          )
        );
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: COMMAND_NAME,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  };
};

// src/commands/GetHistoricalData.ts
import {
  r as r18,
  dateToPythonDateNumber,
  isNumberGuard as isNumberGuard2,
  isObjectGuard as isObjectGuard7,
  parsePythonDate
} from "@crossingminds/utils";
var COMMAND_NAME2 = "GetHistoricalDataInput";
var GET_HISTORICAL_DATA_ENDPOINT = "call-counts/historical";
var GetHistoricalDataCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        searchParams: {
          start_timestamp: this.input.startDate ? dateToPythonDateNumber(this.input.startDate).toString() : void 0,
          end_timestamp: this.input.endDate ? dateToPythonDateNumber(this.input.endDate).toString() : void 0
        },
        method: "get",
        relativeRequestURL: GET_HISTORICAL_DATA_ENDPOINT
      }
    });
    if (isObjectGuard7(payload)) {
      try {
        return {
          counts: r18.required(
            r18.array(
              payload.counts,
              (count) => parseInt(r18.required(r18.string(count)), 10)
            )
          ),
          timestamps: r18.required(
            r18.array(
              payload.timestamps,
              (timestamp) => r18.required(
                isNumberGuard2(timestamp) ? parsePythonDate(timestamp) : void 0
              )
            )
          ),
          ...r18.optionalProperties({
            warnings: r18.array(payload.warnings, (warning) => r18.string(warning))
          })
        };
      } catch {
      }
    }
    throw new ClientError({
      message: "Unexpected data shape" /* dataMismatch */,
      commandId: COMMAND_NAME2,
      errorPayload: JSON.stringify(payload, void 0, 2)
    });
  };
};

// src/commands/GetHistoricalDataByCountType.ts
import {
  r as r19,
  dateToUnixTimestamp as dateToUnixTimestamp6,
  isNumberGuard as isNumberGuard3,
  isObjectGuard as isObjectGuard8,
  parseUnixTimestamp as parseUnixTimestamp2,
  joinPath as joinPath3
} from "@crossingminds/utils";
var COMMAND_NAME3 = "GetHistoricalDataByCountTypeInput";
var GET_HISTORICAL_DATA_ENDPOINT2 = "call-counts/historical";
var GetHistoricalDataByCountTypeCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        searchParams: {
          start_timestamp: this.input.startDate ? dateToUnixTimestamp6(this.input.startDate).toString() : void 0,
          end_timestamp: this.input.endDate ? dateToUnixTimestamp6(this.input.endDate).toString() : void 0
        },
        method: "get",
        relativeRequestURL: joinPath3([
          GET_HISTORICAL_DATA_ENDPOINT2,
          this.input.countType
        ])
      }
    });
    if (isObjectGuard8(payload)) {
      try {
        const output = {
          series: r19.required(
            r19.array(
              payload.series,
              (series) => r19.required(
                r19.object(series, (seriesObject) => ({
                  key: r19.required(r19.string(seriesObject.key)),
                  description: r19.required(r19.string(seriesObject.description)),
                  counts: r19.required(
                    r19.array(
                      seriesObject.counts,
                      (count) => parseInt(r19.required(r19.string(count)), 10)
                    )
                  )
                }))
              )
            )
          ),
          timestamps: r19.required(
            r19.array(
              payload.timestamps,
              (timestamp) => r19.required(
                isNumberGuard3(timestamp) ? parseUnixTimestamp2(timestamp) : void 0
              )
            )
          ),
          ...r19.optionalProperties({
            warnings: r19.array(payload.warnings, (warning) => r19.string(warning))
          })
        };
        return output;
      } catch {
      }
    }
    throw new ClientError({
      message: "Unexpected data shape" /* dataMismatch */,
      commandId: COMMAND_NAME3,
      errorPayload: JSON.stringify(payload, void 0, 2)
    });
  };
};

// src/commands/GetItemById.ts
import { r as r20, run as run15 } from "@crossingminds/utils";
var getItemByIdEndpoint = (itemId) => `items/${itemId}/properties/`;
var GetItemByIdCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "get",
        relativeRequestURL: getItemByIdEndpoint(this.input.itemId)
      }
    });
    const output = run15(() => {
      try {
        return r20.object(payload, ({ item }) => ({
          item: r20.required(r20.object(item, (itemPayload) => itemPayload))
        }));
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: "GetItemByIdCommand",
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  };
};

// src/commands/GetOneItemDataById.ts
var GetOneItemDataByIdCommand = class extends GetItemByIdCommand {
};

// src/commands/GetHistoricalDataByLabel.ts
import {
  dateToUnixTimestamp as dateToUnixTimestamp7,
  isNumberGuard as isNumberGuard4,
  parseUnixTimestamp as parseUnixTimestamp3,
  r as r21,
  run as run16
} from "@crossingminds/utils";
var endpoint = ({ label }) => `call-counts/historical/${label}`;
var COMMAND_ID8 = "GetHistoricalDataByLabelCommand";
var GetHistoricalDataByLabelCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        searchParams: {
          start_timestamp: this.input.startTimestamp ? dateToUnixTimestamp7(this.input.startTimestamp).toString() : void 0,
          end_timestamp: this.input.endTimestamp ? dateToUnixTimestamp7(this.input.endTimestamp).toString() : void 0
        },
        method: "get",
        relativeRequestURL: endpoint({ label: this.input.label })
      }
    });
    const output = run16(() => {
      try {
        return r21.object(payload, ({ timestamps, series, warnings }) => ({
          timestamps: r21.required(
            r21.array(
              timestamps,
              (timestamp) => r21.required(
                isNumberGuard4(timestamp) && parseUnixTimestamp3(timestamp)
              )
            )
          ),
          series: r21.required(
            r21.array(
              series,
              (item) => r21.required(
                r21.object(item, ({ key, description, counts }) => ({
                  key: r21.required(r21.string(key)),
                  description: r21.required(r21.string(description)),
                  counts: r21.required(
                    r21.array(
                      counts,
                      (count) => r21.required(
                        typeof count === "number" ? r21.number(count) : (
                          /* accommodate a bug when numbers are sent as string */
                          r21.number(count == Number(count) && Number(count))
                        )
                        // ^ double equals is intentional
                      )
                    )
                  )
                }))
              )
            )
          ),
          ...r21.optionalProperties({
            warnings: r21.array(warnings, (warning) => r21.string(warning))
          })
        }));
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: COMMAND_ID8,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  };
};

// src/commands/GetPrecomputedRelatedItemRecommendations.ts
import {
  isNumberGuard as isNumberGuard5,
  isStringGuard as isStringGuard3,
  run as run17,
  runtimeTypeChecking as r22
} from "@crossingminds/utils";
var COMMAND_ID9 = "GetPrecomputedRelatedItemRecommendations";
var getPrecomputedRelatedItemRecommendationsEndpoint = (itemId) => `recommendation/precomputed/items/${itemId}/items`;
var GetPrecomputedRelatedItemRecommendationsCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "get",
        relativeRequestURL: getPrecomputedRelatedItemRecommendationsEndpoint(
          this.input.itemId
        ),
        searchParams: [
          ["scenario", this.input.scenario],
          [
            "skip_default_scenario",
            this.input.skipDefaultScenario ? "true" : void 0
          ],
          ["user_id", this.input.userId],
          ["session_id", this.input.sessionId],
          ["table", this.input.table]
        ]
      }
    });
    const output = run17(() => {
      try {
        return r22.required(
          r22.object(payload, ({ items_id, warnings, evaluated_scenarios }) => ({
            itemIds: r22.required(
              r22.array(items_id, (itemId) => {
                if (isStringGuard3(itemId)) {
                  return itemId;
                }
                if (isNumberGuard5(itemId)) {
                  return itemId.toString();
                }
                return void 0;
              })
            ),
            ...r22.optionalProperties({
              warnings: r22.array(warnings, (warning) => r22.string(warning)),
              evaluatedScenarios: typeCheckEvaluatedScenariosValue(evaluated_scenarios)
            })
          }))
        );
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: COMMAND_ID9,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  };
};

// src/commands/GetRelatedItemRecommendations.ts
import {
  isNumberGuard as isNumberGuard6,
  isStringGuard as isStringGuard4,
  runtimeTypeChecking as r23
} from "@crossingminds/utils";
var COMMAND_ID10 = "GetRelatedItemRecommendations";
var getRelatedItemRecommendationsEndpoint = (itemId) => `recommendation/items/${itemId}/items`;
var GetRelatedItemRecommendationsCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "get",
        relativeRequestURL: getRelatedItemRecommendationsEndpoint(
          this.input.itemId
        ),
        searchParams: [
          ["cursor", this.input.cursor],
          ["scenario", this.input.scenario],
          [
            "skip_default_scenario",
            this.input.skipDefaultScenario ? "true" : void 0
          ],
          ["user_id", this.input.userId],
          ["session_id", this.input.sessionId],
          ...convertFiltersToSearchParamEntries(this.input.filters),
          ...convertRerankingToSearchParamEntries(this.input.reranking),
          ...convertMaxResultsToSearchParamEntries(this.input.maxResults)
        ]
      }
    });
    try {
      const output = r23.required(
        r23.object(
          payload,
          ({ items_id, warnings, evaluated_scenarios, next_cursor }) => ({
            itemIds: r23.required(
              r23.array(items_id, (itemId) => {
                if (isStringGuard4(itemId)) {
                  return itemId;
                }
                if (isNumberGuard6(itemId)) {
                  return itemId.toString();
                }
                return void 0;
              })
            ),
            ...r23.optionalProperties({
              warnings: r23.array(warnings, (warning) => r23.string(warning)),
              evaluatedScenarios: typeCheckEvaluatedScenariosValue(evaluated_scenarios),
              nextCursor: r23.string(next_cursor)
            })
          })
        )
      );
      return output;
    } catch {
    }
    throw new ClientError({
      message: "Unexpected data shape" /* dataMismatch */,
      commandId: COMMAND_ID10,
      errorPayload: JSON.stringify(payload, void 0, 2)
    });
  };
};

// src/commands/GetSessionBasedItemRecommendations.ts
import { isNumberGuard as isNumberGuard7, isStringGuard as isStringGuard5, r as r24 } from "@crossingminds/utils";
var SESSION_BASED_ITEM_RECOMMENDATIONS_URL = "recommendation/sessions/items";
var COMMAND_ID11 = "GetSessionBasedItemRecommendationsCommand";
var GetSessionBasedItemRecommendationsCommand = class {
  constructor(input) {
    this.input = input;
  }
  async execute({ requestUtility }) {
    const payload = await requestUtility({
      requestOptions: {
        method: "post:retryable",
        relativeRequestURL: SESSION_BASED_ITEM_RECOMMENDATIONS_URL,
        body: JSON.stringify({
          session_id: this.input.sessionId,
          interactions: formatInteractionsForRequestBody(
            this.input.interactions
          ),
          ratings: formatRatingsForRequestBody(this.input.ratings),
          user_properties: this.input.userProperties,
          user_id: this.input.userId,
          amt: this.input.maxResults,
          cursor: this.input.cursor,
          filters: convertFiltersToRequestBody(this.input.filters),
          reranking: convertRerankingToRequestBody(this.input.reranking),
          scenario: this.input.scenario,
          skip_default_scenario: this.input.skipDefaultScenario,
          exclude_rated_items: this.input.excludeRatedItems
        })
      }
    });
    try {
      const output = r24.required(
        r24.object(
          payload,
          ({ items_id, warnings, evaluated_scenarios, next_cursor }) => ({
            itemIds: r24.required(
              r24.array(items_id, (itemId) => {
                if (isStringGuard5(itemId)) {
                  return itemId;
                }
                if (isNumberGuard7(itemId)) {
                  return itemId.toString();
                }
                return void 0;
              })
            ),
            ...r24.optionalProperties({
              warnings: r24.array(warnings, (warning) => r24.string(warning)),
              evaluatedScenarios: typeCheckEvaluatedScenariosValue(evaluated_scenarios),
              nextCursor: r24.string(next_cursor)
            })
          })
        )
      );
      return output;
    } catch {
    }
    throw new ClientError({
      message: "Unexpected data shape" /* dataMismatch */,
      commandId: COMMAND_ID11,
      errorPayload: JSON.stringify(payload, void 0, 2)
    });
  }
};

// src/commands/GetSessionBasedItemPropertyRecommendations.ts
import { r as r25, run as run18 } from "@crossingminds/utils";
var GetSessionBasedItemPropertyRecommendationsURL = ({
  propertyName
}) => `recommendation/sessions/items-properties/${propertyName}/`;
var GET_SESSION_BASED_ITEM_PROPERTY_RECOMMENDATIONS_COMMAND_NAME = "GetSessionBasedItemPropertyRecommendationsCommand";
var GetSessionBasedItemPropertyRecommendationsCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "post:retryable",
        relativeRequestURL: GetSessionBasedItemPropertyRecommendationsURL({
          propertyName: this.input.propertyName
        }),
        body: JSON.stringify({
          interactions: formatInteractionsForRequestBody(
            this.input.interactions
          ),
          ratings: formatRatingsForRequestBody(this.input.ratings),
          user_properties: this.input.userProperties,
          scenario: this.input.scenario,
          session_id: this.input.sessionId,
          user_id: this.input.userId,
          amt: this.input.maxResults,
          filters: convertFiltersToRequestBody(this.input.filters)
        })
      }
    });
    const output = run18(() => {
      try {
        return r25.object(payload, ({ warnings, properties }) => ({
          itemProperties: r25.required(
            r25.array(properties, (property) => r25.string(property))
          ),
          ...r25.optionalProperties({
            warnings: r25.array(warnings, (warning) => r25.string(warning))
          })
        }));
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        errorPayload: JSON.stringify(payload, void 0, 2),
        commandId: GET_SESSION_BASED_ITEM_PROPERTY_RECOMMENDATIONS_COMMAND_NAME
      });
    }
    return output;
  };
};

// src/commands/GetSessionWithContextItemBasedItemRecommendations.ts
import { r as r26, run as run19 } from "@crossingminds/utils";
var GET_SESSION_WITH_CONTEXT_BASED_ITEM_RECOMMENDATIONS_URL = "recommendation/context-items/sessions/items";
var GET_SESSION_WITH_CONTEXT_BASED_ITEM_RECOMMENDATIONS_COMMAND_NAME = "GetSessionWithContextItemBasedItemRecommendationsCommand";
var GetSessionWithContextItemBasedItemRecommendationsCommand = class {
  constructor(input) {
    this.input = input;
  }
  async execute({ requestUtility }) {
    const payload = await requestUtility({
      requestOptions: {
        method: "post:retryable",
        relativeRequestURL: GET_SESSION_WITH_CONTEXT_BASED_ITEM_RECOMMENDATIONS_URL,
        body: JSON.stringify({
          context_items: convertContextItemsToRequestBody(
            this.input.contextItems
          ),
          scenario: this.input.scenario,
          skip_default_scenario: this.input.skipDefaultScenario,
          cursor: this.input.cursor,
          session_id: this.input.sessionId,
          interactions: formatInteractionsForRequestBody(
            this.input.interactions
          ),
          ratings: formatRatingsForRequestBody(this.input.ratings),
          user_properties: this.input.userProperties,
          user_id: this.input.userId,
          amt: this.input.maxResults,
          filters: convertFiltersToRequestBody(this.input.filters),
          reranking: convertRerankingToRequestBody(this.input.reranking),
          exclude_rated_items: this.input.excludeRatedItems
        })
      }
    });
    const output = run19(() => {
      try {
        return r26.object(
          payload,
          ({ warnings, items_id, evaluated_scenarios, next_cursor }) => ({
            itemIds: r26.required(r26.array(items_id, r26.string)),
            ...r26.optionalProperties({
              warnings: r26.array(warnings, r26.string),
              evaluatedScenarios: typeCheckEvaluatedScenariosValue(evaluated_scenarios),
              nextCursor: r26.string(next_cursor)
            })
          })
        );
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: GET_SESSION_WITH_CONTEXT_BASED_ITEM_RECOMMENDATIONS_COMMAND_NAME,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  }
};

// src/commands/GetUserBasedItemRecommendations.ts
import { isNumberGuard as isNumberGuard8, isStringGuard as isStringGuard6, r as r27 } from "@crossingminds/utils";
var getUserBasedItemRecommendationsUrl = (userId) => `recommendation/users/${userId}/items`;
var COMMAND_ID12 = "GetUserBasedItemRecommendationsCommand";
var GetUserBasedItemRecommendationsCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "get",
        relativeRequestURL: getUserBasedItemRecommendationsUrl(
          this.input.userId
        ),
        searchParams: [
          ["cursor", this.input.cursor],
          ["scenario", this.input.scenario],
          [
            "skip_default_scenario",
            this.input.skipDefaultScenario ? "true" : void 0
          ],
          [
            "exclude_rated_items",
            this.input.excludeRatedItems ? "true" : void 0
          ],
          ...convertFiltersToSearchParamEntries(this.input.filters),
          ...convertRerankingToSearchParamEntries(this.input.reranking),
          ...convertMaxResultsToSearchParamEntries(this.input.maxResults)
        ]
      }
    });
    try {
      const output = r27.required(
        r27.object(
          payload,
          ({ items_id, warnings, evaluated_scenarios, next_cursor }) => ({
            itemIds: r27.required(
              r27.array(items_id, (itemId) => {
                if (isStringGuard6(itemId)) {
                  return itemId;
                }
                if (isNumberGuard8(itemId)) {
                  return itemId.toString();
                }
                return void 0;
              })
            ),
            ...r27.optionalProperties({
              warnings: r27.array(warnings, (warning) => r27.string(warning)),
              evaluatedScenarios: typeCheckEvaluatedScenariosValue(evaluated_scenarios),
              nextCursor: r27.string(next_cursor)
            })
          })
        )
      );
      return output;
    } catch {
    }
    throw new ClientError({
      message: "Unexpected data shape" /* dataMismatch */,
      commandId: COMMAND_ID12,
      errorPayload: JSON.stringify(payload, void 0, 2)
    });
  };
};

// src/commands/GetUserBasedPropertyRecommendations.ts
import { r as r28, run as run20 } from "@crossingminds/utils";
var GET_USER_BASED_PROPERTY_RECOMMENDATIONS_COMMAND_NAME = "GetUserBasedPropertyRecommendationsCommand";
var getUserBasedPropertyRecommendationsCommandURL = ({
  propertyName,
  userId
}) => `recommendation/users/${userId}/items-properties/${propertyName}`;
var GetUserBasedPropertyRecommendationsCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "get",
        relativeRequestURL: getUserBasedPropertyRecommendationsCommandURL({
          propertyName: this.input.propertyName,
          userId: this.input.userId
        }),
        searchParams: {
          scenario: this.input.scenario,
          amt: typeof this.input.maxResults === "number" ? String(this.input.maxResults) : void 0,
          ...convertFiltersToSearchParamEntries(this.input.filters)
        }
      }
    });
    const output = run20(() => {
      try {
        return r28.object(payload, ({ warnings, properties }) => ({
          itemProperties: r28.required(
            r28.array(properties, (property) => r28.string(property))
          ),
          ...r28.optionalProperties({
            warnings: r28.array(warnings, (warning) => r28.string(warning))
          })
        }));
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: GET_USER_BASED_PROPERTY_RECOMMENDATIONS_COMMAND_NAME,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
  };
};

// src/commands/GetUserById.ts
import { r as r29, run as run21 } from "@crossingminds/utils";
var getUserByIdEndpoint = (userId) => `users/${userId}/properties/`;
var GetUserByIdCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "get",
        relativeRequestURL: getUserByIdEndpoint(this.input.userId)
      }
    });
    const output = run21(() => {
      try {
        return r29.object(payload, ({ user }) => ({
          user: r29.required(r29.object(user, (userPayload) => userPayload))
        }));
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: "GetUserByIdCommand",
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  };
};

// src/commands/GetUserWithContextItemBasedItemRecommendations.ts
import { r as r30, run as run22 } from "@crossingminds/utils";
var getUserWithContextItemBasedItemRecommendationsURL = ({
  userId
}) => `recommendation/context-items/users/${userId}/items/`;
var GET_USER_WITH_CONTEXT_ITEM_BASED_ITEM_RECOMMENDATIONS_COMMAND_NAME = "GetUserWithContextItemBasedItemRecommendationsCommand";
var GetUserWithContextItemBasedItemRecommendationsCommand = class {
  constructor(input) {
    this.input = input;
  }
  async execute({ requestUtility }) {
    const payload = await requestUtility({
      requestOptions: {
        method: "post:retryable",
        relativeRequestURL: getUserWithContextItemBasedItemRecommendationsURL({
          userId: this.input.userId
        }),
        body: JSON.stringify({
          context_items: convertContextItemsToRequestBody(
            this.input.contextItems
          ),
          scenario: this.input.scenario,
          skip_default_scenario: this.input.skipDefaultScenario,
          cursor: this.input.cursor,
          filters: convertFiltersToRequestBody(this.input.filters),
          reranking: convertRerankingToRequestBody(this.input.reranking),
          exclude_rated_items: this.input.excludeRatedItems,
          amt: this.input.maxResults
        })
      }
    });
    const output = run22(() => {
      try {
        return r30.object(
          payload,
          ({ warnings, items_id, evaluated_scenarios, next_cursor }) => ({
            itemIds: r30.required(r30.array(items_id, (id) => r30.string(id))),
            ...r30.optionalProperties({
              warnings: r30.array(warnings, (warning) => r30.string(warning)),
              nextCursor: r30.string(next_cursor),
              evaluatedScenarios: typeCheckEvaluatedScenariosValue(evaluated_scenarios)
            })
          })
        );
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: GET_USER_WITH_CONTEXT_ITEM_BASED_ITEM_RECOMMENDATIONS_COMMAND_NAME,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  }
};

// src/commands/ListAllAccounts.ts
import { parseUnixTimestamp as parseUnixTimestamp4, r as r31, run as run23 } from "@crossingminds/utils";
var LIST_ALL_ACCOUNTS_URL = "organizations/current/accounts";
var LIST_ALL_ACCOUNTS_COMMAND_NAME = "ListAllAccounts";
var ListAllAccountsCommand = class {
  async execute({ requestUtility }) {
    const payload = await requestUtility({
      requestOptions: {
        method: "get",
        relativeRequestURL: LIST_ALL_ACCOUNTS_URL
      }
    });
    const output = run23(() => {
      try {
        return r31.required(
          r31.object(
            payload,
            ({ individual_accounts, service_accounts, warnings }) => ({
              individualAccounts: r31.required(
                r31.array(
                  individual_accounts,
                  (account) => r31.object(
                    account,
                    ({ role, first_name, last_name, email, metadata }) => ({
                      role: r31.required(r31.oneOf(role, ACCOUNT_ROLES)),
                      email: r31.required(r31.string(email)),
                      ...r31.optionalProperties({
                        firstName: r31.string(first_name),
                        lastName: r31.string(last_name),
                        metadata: r31.object(
                          metadata,
                          (unknownObject) => unknownObject
                        )
                      })
                    })
                  )
                )
              ),
              serviceAccounts: r31.required(
                r31.array(
                  service_accounts,
                  (account) => r31.object(
                    account,
                    ({ role, name: serviceAccountName, keys }) => ({
                      role: r31.required(r31.oneOf(role, ACCOUNT_ROLES)),
                      name: r31.required(r31.string(serviceAccountName)),
                      keys: r31.required(
                        r31.array(
                          keys,
                          (key) => r31.object(
                            key,
                            ({
                              id,
                              created_timestamp,
                              expiration_timestamp,
                              is_expired,
                              metadata
                            }) => ({
                              id: r31.required(r31.string(id)),
                              createdTimestamp: r31.required(
                                parseUnixTimestamp4(r31.number(created_timestamp))
                              ),
                              expirationTimestamp: parseUnixTimestamp4(
                                r31.number(expiration_timestamp)
                              ),
                              isExpired: r31.required(r31.boolean(is_expired)),
                              metadata: r31.required(
                                r31.object(metadata, (metadataObj) => metadataObj)
                              )
                            })
                          )
                        )
                      )
                    })
                  )
                )
              ),
              ...r31.optionalProperties({
                warnings: r31.array(warnings, r31.string)
              })
            })
          )
        );
      } catch {
        return void 0;
      }
    });
    if (output === void 0) {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: LIST_ALL_ACCOUNTS_COMMAND_NAME,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  }
};

// src/commands/ListAllDatabases.ts
import { isObjectGuard as isObjectGuard9 } from "@crossingminds/utils";
var LIST_ALL_DATABASES_ENDPOINT = "databases";
var ListAllDatabasesCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        relativeRequestURL: LIST_ALL_DATABASES_ENDPOINT,
        method: "get",
        searchParams: {
          ...typeof this.input?.page === "number" ? { page: String(this.input.page) } : {},
          ...typeof this.input?.maxResults === "number" ? { amt: String(this.input?.maxResults) } : {}
        }
      }
    });
    if (isObjectGuard9(payload) && typeof payload.has_next === "boolean" && typeof payload.next_page === "number" && Array.isArray(payload.databases)) {
      const databases = [];
      payload.databases.forEach((database) => {
        const formattedDatabase = typeCheckDatabaseProperty(database);
        if (formattedDatabase) {
          databases.push(formattedDatabase);
        } else {
          throw new ClientError({
            message: "Unexpected data shape" /* dataMismatch */,
            errorPayload: `ListAllDatabasesCommand
${JSON.stringify(
              payload,
              void 0,
              2
            )}`
          });
        }
      });
      const output = {
        hasNextPage: payload.has_next,
        nextPageNumber: payload.next_page,
        databases,
        ...Array.isArray(payload.warnings) ? {
          warnings: payload.warnings.filter(
            (item) => typeof item === "string"
          )
        } : {}
      };
      return output;
    }
    throw new ClientError({
      message: "Unexpected data shape" /* dataMismatch */,
      errorPayload: `ListAllDatabasesCommand
${JSON.stringify(
        payload,
        void 0,
        2
      )}`
    });
  };
};

// src/commands/ListAllItemProperties.ts
import { r as r32, run as run24 } from "@crossingminds/utils";
var LIST_ALL_ITEM_PROPERTIES_URL = "items-properties";
var COMMAND_ID13 = "ListAllItemProperties";
var ListAllItemPropertiesCommand = class {
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "get",
        relativeRequestURL: LIST_ALL_ITEM_PROPERTIES_URL
      }
    });
    const output = run24(() => {
      try {
        return r32.required(
          r32.object(payload, ({ properties, warnings }) => ({
            properties: r32.required(
              r32.array(
                properties,
                (property) => r32.required(
                  r32.object(
                    property,
                    ({ property_name, value_type, repeated }) => ({
                      propertyName: r32.required(r32.string(property_name)),
                      valueType: r32.required(r32.oneOf(value_type, propertyTypes)),
                      repeated: r32.required(r32.boolean(repeated))
                    })
                  )
                )
              )
            ),
            ...r32.optionalProperties({ warnings: r32.array(warnings, r32.string) })
          }))
        );
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: COMMAND_ID13,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  };
};

// src/commands/ListAllRatingsOfOneUser.ts
import { parseUnixTimestamp as parseUnixTimestamp5, r as r33, run as run25 } from "@crossingminds/utils";
var listAllRatingsOfOneUserEndpoint = (userId) => `users/${userId}/ratings/`;
var ListAllRatingsOfOneUserCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "get",
        relativeRequestURL: listAllRatingsOfOneUserEndpoint(this.input.userId),
        searchParams: {
          ...typeof this.input.page === "number" ? { page: String(this.input.page) } : {},
          ...typeof this.input.maxResults === "number" ? { amt: String(this.input.maxResults) } : {}
        }
      }
    });
    const output = run25(() => {
      try {
        return r33.object(
          payload,
          ({ has_next, next_page, user_ratings, warnings }) => ({
            hasNext: r33.required(r33.boolean(has_next)),
            nextPage: r33.required(r33.number(next_page)),
            userRatings: r33.required(
              r33.array(
                user_ratings,
                (userRating) => r33.object(userRating, ({ item_id, rating, timestamp }) => ({
                  itemId: normalizeId(item_id),
                  rating: r33.required(r33.number(rating)),
                  timestamp: r33.required(parseUnixTimestamp5(r33.number(timestamp)))
                }))
              ) ?? []
            ),
            ...r33.optionalProperties({
              warnings: r33.array(warnings, (warning) => r33.string(warning))
            })
          })
        );
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: "ListAllRatingsOfOneUserCommand",
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  };
};

// src/commands/ListAllUserProperties.ts
import { r as r34, run as run26 } from "@crossingminds/utils";
var LIST_ALL_USER_PROPERTIES_URL = "users-properties";
var COMMAND_ID14 = "ListAllUserProperties";
var ListAllUserPropertiesCommand = class {
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "get",
        relativeRequestURL: LIST_ALL_USER_PROPERTIES_URL
      }
    });
    const output = run26(() => {
      try {
        return r34.required(
          r34.object(payload, ({ properties, warnings }) => ({
            properties: r34.required(
              r34.array(
                properties,
                (property) => r34.required(
                  r34.object(
                    property,
                    ({ property_name, value_type, repeated, indexed }) => ({
                      propertyName: r34.required(r34.string(property_name)),
                      valueType: r34.required(r34.oneOf(value_type, propertyTypes)),
                      repeated: r34.required(r34.boolean(repeated)),
                      indexed: r34.required(r34.boolean(indexed))
                    })
                  )
                )
              )
            ),
            ...r34.optionalProperties({ warnings: r34.array(warnings, r34.string) })
          }))
        );
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: COMMAND_ID14,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  };
};

// src/commands/ListInteractionsOfAllUsers.ts
import {
  r as r35,
  run as run27,
  dateToUnixTimestamp as dateToUnixTimestamp8,
  parseUnixTimestamp as parseUnixTimestamp6
} from "@crossingminds/utils";
var LIST_INTERACTIONS_OF_ALL_USERS_URL = "interactions-bulk";
var COMMAND_ID15 = "ListInteractionsOfAllUsers";
var ListInteractionsOfAllUsersCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "get",
        relativeRequestURL: LIST_INTERACTIONS_OF_ALL_USERS_URL,
        searchParams: [
          ["cursor", this.input?.cursor],
          [
            "start_timestamp",
            this.input?.startTimestamp ? dateToUnixTimestamp8(this.input?.startTimestamp).toString() : void 0
          ],
          [
            "end_timestamp",
            this.input?.endTimestamp ? dateToUnixTimestamp8(this.input?.endTimestamp).toString() : void 0
          ],
          ...convertMaxResultsToSearchParamEntries(this.input?.maxResults)
        ]
      }
    });
    const output = run27(() => {
      try {
        return r35.required(
          r35.object(
            payload,
            ({ interactions, has_next, next_cursor, warnings }) => ({
              interactions: r35.required(
                r35.array(
                  interactions,
                  (interaction) => r35.required(
                    r35.object(
                      interaction,
                      ({
                        item_id,
                        interaction_type,
                        timestamp,
                        properties,
                        user_id
                      }) => ({
                        itemId: normalizeId(item_id),
                        interactionType: r35.required(r35.string(interaction_type)),
                        timestamp: r35.required(
                          parseUnixTimestamp6(r35.number(timestamp))
                        ),
                        properties: r35.object(
                          properties,
                          (propertiesObject) => propertiesObject
                        ),
                        userId: normalizeId(user_id)
                      })
                    )
                  )
                )
              ),
              hasNext: r35.required(r35.boolean(has_next)),
              ...r35.optionalProperties({
                warnings: r35.array(warnings, r35.string),
                nextCursor: r35.string(next_cursor)
              })
            })
          )
        );
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: COMMAND_ID15,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  };
};

// src/commands/ListInteractionsOfOneUser.ts
import { parseUnixTimestamp as parseUnixTimestamp7, r as r36, run as run28 } from "@crossingminds/utils";
var listInteractionsOfOneUserEndpoint = (userId) => `users/${userId}/interactions-bulk`;
var ListInteractionsOfOneUserCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "get",
        relativeRequestURL: listInteractionsOfOneUserEndpoint(
          this.input.userId
        ),
        searchParams: {
          ...typeof this.input.cursor === "string" ? { cursor: this.input.cursor } : {},
          ...typeof this.input.maxResults === "number" ? { amt: String(this.input?.maxResults) } : {}
        }
      }
    });
    const output = run28(() => {
      try {
        return r36.required(
          r36.object(
            payload,
            ({ interactions, has_next, next_cursor, warnings }) => ({
              interactions: r36.required(
                r36.array(
                  interactions,
                  (interaction) => r36.required(
                    r36.object(
                      interaction,
                      ({
                        item_id,
                        interaction_type,
                        timestamp,
                        properties
                      }) => ({
                        itemId: normalizeId(item_id),
                        interactionType: r36.required(r36.string(interaction_type)),
                        timestamp: r36.required(
                          parseUnixTimestamp7(r36.number(timestamp))
                        ),
                        properties: r36.object(
                          properties,
                          (propertiesObject) => propertiesObject
                        )
                      })
                    )
                  )
                )
              ),
              hasNext: r36.required(r36.boolean(has_next)),
              ...r36.optionalProperties({
                warnings: r36.array(warnings, r36.string),
                nextCursor: r36.string(next_cursor)
              })
            })
          )
        );
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: "ListInteractionsOfOneUserCommand",
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  };
};

// src/commands/ListPropertiesOfAllItems.ts
import { r as r37, run as run29 } from "@crossingminds/utils";
var LIST_PROPERTIES_OF_ALL_ITEMS_URL = "items-bulk/properties";
var COMMAND_ID16 = "ListPropertiesOfAllItems";
var ListPropertiesOfAllItemsCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "get",
        relativeRequestURL: LIST_PROPERTIES_OF_ALL_ITEMS_URL,
        searchParams: [
          ["cursor", this.input?.cursor],
          ["properties", this.input?.properties],
          ...convertFiltersToSearchParamEntries(this.input?.filters),
          ...convertMaxResultsToSearchParamEntries(this.input?.maxResults)
        ]
      }
    });
    const output = run29(() => {
      try {
        return r37.required(
          r37.object(
            payload,
            ({ items, items_m2m, has_next, next_cursor, warnings }) => ({
              items: r37.required(
                r37.array(
                  items,
                  (item) => r37.required(
                    r37.object(item, ({ item_id, ...properties }) => ({
                      ...properties,
                      itemId: r37.required(
                        r37.string(item_id) || r37.number(item_id)?.toString()
                      )
                    }))
                  )
                )
              ),
              hasNext: r37.required(r37.boolean(has_next)),
              ...r37.optionalProperties({
                warnings: r37.array(warnings, r37.string),
                nextCursor: r37.string(next_cursor),
                itemsM2M: r37.array(
                  items_m2m,
                  (itemM2M) => r37.required(
                    r37.object(itemM2M, ({ name: itemName, array }) => ({
                      name: r37.required(r37.string(itemName)),
                      array: r37.required(
                        r37.array(
                          array,
                          (arrItem) => r37.required(
                            r37.object(arrItem, ({ item_index, value_id }) => ({
                              itemIndex: r37.required(r37.number(item_index)),
                              valueId: r37.required(r37.string(value_id))
                            }))
                          )
                        )
                      )
                    }))
                  )
                )
              })
            })
          )
        );
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: COMMAND_ID16,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  };
};

// src/commands/ListPropertiesOfAllUsers.ts
import { r as r38, run as run30 } from "@crossingminds/utils";
var LIST_PROPERTIES_OF_ALL_USERS_URL = "users-bulk/properties";
var COMMAND_ID17 = "ListPropertiesOfAllUsers";
var ListPropertiesOfAllUsersCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "get",
        relativeRequestURL: LIST_PROPERTIES_OF_ALL_USERS_URL,
        searchParams: [
          ["cursor", this.input?.cursor],
          ["properties", this.input?.properties],
          ...convertFiltersToSearchParamEntries(this.input?.filters),
          ...convertMaxResultsToSearchParamEntries(this.input?.maxResults)
        ]
      }
    });
    const output = run30(() => {
      try {
        return r38.required(
          r38.object(
            payload,
            ({ users, users_m2m, has_next, next_cursor, warnings }) => ({
              users: r38.required(
                r38.array(
                  users,
                  (user) => r38.required(
                    r38.object(user, ({ user_id, ...properties }) => ({
                      ...properties,
                      userId: r38.required(
                        r38.string(user_id) || r38.number(user_id)?.toString()
                      )
                    }))
                  )
                )
              ),
              hasNext: r38.required(r38.boolean(has_next)),
              ...r38.optionalProperties({
                warnings: r38.array(warnings, r38.string),
                nextCursor: r38.string(next_cursor),
                usersM2M: r38.array(
                  users_m2m,
                  (userM2M) => r38.required(
                    r38.object(userM2M, ({ name: userName, array }) => ({
                      name: r38.required(r38.string(userName)),
                      array: r38.required(
                        r38.array(
                          array,
                          (arrUser) => r38.required(
                            r38.object(arrUser, ({ user_index, value_id }) => ({
                              userIndex: r38.required(r38.number(user_index)),
                              valueId: r38.required(r38.string(value_id))
                            }))
                          )
                        )
                      )
                    }))
                  )
                )
              })
            })
          )
        );
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: COMMAND_ID17,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  };
};

// src/commands/ListPropertiesOfManyItemsById.ts
import { r as r39, run as run31 } from "@crossingminds/utils";
var LIST_PROPERTIES_OF_MANY_ITEMS_BY_ID_URL = "items-bulk/properties/list";
var COMMAND_ID18 = "ListPropertiesOfManyItemsById";
var ListPropertiesOfManyItemsByIdCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "post:retryable",
        relativeRequestURL: LIST_PROPERTIES_OF_MANY_ITEMS_BY_ID_URL,
        body: JSON.stringify({
          items_id: this.input.itemIds,
          properties: this.input.properties
        })
      }
    });
    const output = run31(() => {
      try {
        return r39.required(
          r39.object(payload, ({ items, warnings }) => ({
            items: r39.required(
              r39.array(
                items,
                (item) => r39.required(
                  r39.object(
                    item,
                    ({ item_id, ...rest }) => ({
                      itemId: normalizeId(item_id),
                      ...rest
                    })
                  )
                )
              )
            ),
            ...r39.optionalProperties({ warnings: r39.array(warnings, r39.string) })
          }))
        );
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: COMMAND_ID18,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  };
};

// src/commands/ListRatingsOfAllUsers.ts
import { r as r40, run as run32, parseUnixTimestamp as parseUnixTimestamp8 } from "@crossingminds/utils";
var LIST_RATINGS_OF_ALL_USERS_URL = "ratings-bulk";
var COMMAND_ID19 = "ListRatingsOfAllUsers";
var ListRatingsOfAllUsersCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "get",
        relativeRequestURL: LIST_RATINGS_OF_ALL_USERS_URL,
        searchParams: [
          ["cursor", this.input?.cursor],
          ...convertMaxResultsToSearchParamEntries(this.input?.maxResults)
        ]
      }
    });
    const output = run32(() => {
      try {
        return r40.required(
          r40.object(payload, ({ ratings, has_next, next_cursor, warnings }) => ({
            ratings: r40.required(
              r40.array(
                ratings,
                (rating) => r40.required(
                  r40.object(
                    rating,
                    ({ user_id, item_id, rating: ratingValue, timestamp }) => ({
                      userId: normalizeId(user_id),
                      itemId: normalizeId(item_id),
                      rating: r40.required(r40.number(ratingValue)),
                      timestamp: r40.required(
                        parseUnixTimestamp8(r40.number(timestamp))
                      )
                    })
                  )
                )
              )
            ),
            hasNext: r40.required(r40.boolean(has_next)),
            ...r40.optionalProperties({
              warnings: r40.array(warnings, r40.string),
              nextCursor: r40.string(next_cursor)
            })
          }))
        );
      } catch {
        return void 0;
      }
    });
    if (typeof output === "undefined") {
      throw new ClientError({
        message: "Unexpected data shape" /* dataMismatch */,
        commandId: COMMAND_ID19,
        errorPayload: JSON.stringify(payload, void 0, 2)
      });
    }
    return output;
  };
};

// src/commands/ListRecentBackgroundTasks.ts
import { r as r41, isObjectGuard as isObjectGuard10, parsePythonDate as parsePythonDate2 } from "@crossingminds/utils";
var EXECUTION_STATUSES = [
  "REQUEST_SENT",
  "RUNNING",
  "FAILED",
  "CANCELLED_REQUESTED",
  "CANCELLED",
  "COMPLETED"
];
var COMMAND_NAME4 = "ListRecentBackgroundTasksInput";
var listRecentBackgroundTasksEndPoint = (taskName) => `tasks/${taskName}/recents`;
var ListRecentBackgroundTasksCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        searchParams: {
          amt: this.input.maxResults?.toString(),
          page: this.input.page?.toString()
        },
        method: "get",
        relativeRequestURL: listRecentBackgroundTasksEndPoint(
          this.input.taskName
        )
      }
    });
    if (isObjectGuard10(payload)) {
      try {
        return {
          hasNext: r41.required(r41.boolean(payload.has_next)),
          nextPage: r41.required(r41.number(payload.next_page)),
          tasks: r41.required(
            r41.array(
              payload.tasks,
              (task) => r41.object(
                task,
                (taskObject) => r41.optionalProperties({
                  warnings: r41.array(
                    taskObject.warnings,
                    (warning) => r41.string(warning)
                  ),
                  taskId: r41.string(taskObject.task_id),
                  name: r41.string(taskObject.name),
                  startTime: parsePythonDate2(r41.number(taskObject.start_time)),
                  status: r41.oneOf(taskObject.status, EXECUTION_STATUSES),
                  progress: r41.string(taskObject.progress)
                })
              )
            ) ?? []
          ),
          ...r41.optionalProperties({
            warnings: r41.array(payload.warnings, (warning) => r41.string(warning))
          })
        };
      } catch {
      }
    }
    throw new ClientError({
      message: "Unexpected data shape" /* dataMismatch */,
      commandId: COMMAND_NAME4,
      errorPayload: JSON.stringify(payload, void 0, 2)
    });
  };
};

// src/commands/PartialUpdateIndividualAccount.ts
import { r as r42 } from "@crossingminds/utils";
var PARTIAL_UPDATE_INDIVIDUAL_ACCOUNT_URL = "accounts/individual";
var PartialUpdateIndividualAccountCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "patch",
        relativeRequestURL: PARTIAL_UPDATE_INDIVIDUAL_ACCOUNT_URL,
        body: JSON.stringify({
          email: this.input.email,
          password: this.input.password,
          first_name: this.input.firstName,
          last_name: this.input.lastName,
          metadata: this.input.metadata
        })
      }
    });
    const output = r42.object(
      payload,
      ({ warnings }) => r42.optionalProperties({
        warnings: r42.array(warnings, r42.string)
      })
    );
    return output;
  };
};

// src/commands/ResetPassword.ts
import { dateToUnixTimestamp as dateToUnixTimestamp9 } from "@crossingminds/utils";
var RESET_PASSWORD_ENDPOINT = "accounts/individual/reset";
var ResetPasswordCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    await requestUtility({
      requestOptions: {
        method: "post",
        relativeRequestURL: RESET_PASSWORD_ENDPOINT,
        body: JSON.stringify({
          email: this.input.email,
          code: this.input.code,
          new_password: this.input.newPassword,
          code_expiration_timestamp: dateToUnixTimestamp9(this.input.expiration)
        })
      }
    });
    return void 0;
  };
};

// src/commands/ResolveOneAnonymousSession.ts
import { dateToUnixTimestamp as dateToUnixTimestamp10 } from "@crossingminds/utils";
var ResolveOneAnonymousSessionEndpoint = ({
  sessionId
}) => `sessions/${sessionId}/resolve`;
var ResolveOneAnonymousSessionCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    await requestUtility({
      requestOptions: {
        relativeRequestURL: ResolveOneAnonymousSessionEndpoint({
          sessionId: this.input.sessionId
        }),
        method: "post:keepalive",
        body: JSON.stringify({
          user_id: this.input.userId,
          timestamp: this.input.timestamp ? dateToUnixTimestamp10(this.input.timestamp).toString() : void 0
        })
      }
    });
    return void 0;
  };
};

// src/commands/SendResetPasswordEmail.ts
var SEND_RESET_PASSWORD_EMAIL_ENDPOINT = "accounts/individual/send-reset-password-email";
var SendResetPasswordEmailCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    await requestUtility({
      requestOptions: {
        method: "post",
        relativeRequestURL: SEND_RESET_PASSWORD_EMAIL_ENDPOINT,
        body: JSON.stringify({
          email: this.input.email,
          email_redirect_url: this.input.emailRedirectUrl
        })
      }
    });
    return void 0;
  };
};

// src/commands/UpdateItemPropertiesInBulk.ts
import { r as r43, run as run33 } from "@crossingminds/utils";
var UPDATE_ITEM_PROPERTIES_IN_BULK_URL = "items-bulk/properties";
var RAW_INGESTION_UPDATE_ITEM_PROPERTIES_IN_BULK_URL = "raw-ingestion/items-bulk/properties";
var UpdateItemPropertiesInBulkCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const { relativeRequestURL, connection } = run33(() => {
      switch (this.input.pipeline) {
        case void 0:
          return {
            relativeRequestURL: RAW_INGESTION_UPDATE_ITEM_PROPERTIES_IN_BULK_URL,
            connection: this.input.connection
          };
        case "raw":
          return {
            relativeRequestURL: RAW_INGESTION_UPDATE_ITEM_PROPERTIES_IN_BULK_URL,
            connection: this.input.connection
          };
        case "direct":
          return {
            relativeRequestURL: UPDATE_ITEM_PROPERTIES_IN_BULK_URL,
            connection: void 0
          };
      }
    });
    const payload = await requestUtility({
      requestOptions: {
        method: "patch",
        relativeRequestURL,
        body: JSON.stringify({
          items: convertItemsToRequestBody(this.input.items),
          items_m2m: convertItemsM2MToRequestBody(this.input.itemsM2M),
          wait_for_completion: this.input.waitForCompletion,
          create_if_missing: this.input.createIfMissing,
          connection
        })
      }
    });
    const output = r43.object(
      payload,
      ({ warnings }) => r43.optionalProperties({
        warnings: r43.array(warnings, r43.string)
      })
    );
    return output;
  };
};
function convertItemsToRequestBody(items) {
  if (typeof items === "undefined") {
    return void 0;
  }
  return items.map(({ itemId, ...rest }) => ({
    item_id: itemId,
    ...rest
  }));
}
function convertItemsM2MToRequestBody(itemsM2M) {
  if (typeof itemsM2M === "undefined") {
    return void 0;
  }
  return itemsM2M.map(({ name: itemName, array }) => ({
    name: itemName,
    array: array.map(({ itemIndex, valueId }) => ({
      item_index: itemIndex,
      value_id: valueId
    }))
  }));
}

// src/commands/UpdateUserPropertiesInBulk.ts
import { r as r44 } from "@crossingminds/utils";
var UPDATE_USER_PROPERTIES_IN_BULK_URL = "users-bulk/properties";
var UpdateUserPropertiesInBulkCommand = class {
  constructor(input) {
    this.input = input;
  }
  execute = async ({ requestUtility }) => {
    const payload = await requestUtility({
      requestOptions: {
        method: "patch",
        relativeRequestURL: UPDATE_USER_PROPERTIES_IN_BULK_URL,
        body: JSON.stringify({
          users: convertUsersToRequestBody(this.input.users),
          items_m2m: convertUsersM2MToRequestBody(this.input.usersM2M),
          wait_for_completion: this.input.waitForCompletion,
          create_if_missing: this.input.createIfMissing
        })
      }
    });
    const output = r44.object(
      payload,
      ({ warnings }) => r44.optionalProperties({
        warnings: r44.array(warnings, r44.string)
      })
    );
    return output;
  };
};
function convertUsersToRequestBody(users) {
  if (typeof users === "undefined") {
    return void 0;
  }
  return users.map(({ userId, ...rest }) => ({
    user_id: userId,
    ...rest
  }));
}
function convertUsersM2MToRequestBody(usersM2M) {
  if (typeof usersM2M === "undefined") {
    return void 0;
  }
  return usersM2M.map(({ name: userName, array }) => ({
    name: userName,
    array: array.map(({ userIndex, valueId }) => ({
      user_index: userIndex,
      value_id: valueId
    }))
  }));
}
export {
  APIError,
  ClientError,
  ClientErrorMessages,
  CreateIndividualAccountCommand,
  CreateInteractionsForManyUsersCommand,
  CreateOneAnonymousSessionInteractionCommand,
  CreateOneUserInteractionCommand,
  CreateServiceAccountCommand,
  CreateServiceAccountKeyCommand,
  CredentialStore,
  DeleteIndividualAccountCommand,
  DeleteServiceAccountCommand,
  DeleteServiceAccountKeyCommand,
  FILTER_OPERATORS,
  GetAllScenariosCommand,
  GetCurrentDatabaseCommand,
  GetHistoricalDataByCountTypeCommand,
  GetHistoricalDataByLabelCommand,
  GetHistoricalDataCommand,
  GetItemByIdCommand,
  GetOneItemDataByIdCommand,
  GetPrecomputedRelatedItemRecommendationsCommand,
  GetRelatedItemRecommendationsCommand,
  GetSessionBasedItemPropertyRecommendationsCommand,
  GetSessionBasedItemRecommendationsCommand,
  GetSessionWithContextItemBasedItemRecommendationsCommand,
  GetUserBasedItemRecommendationsCommand,
  GetUserBasedPropertyRecommendationsCommand,
  GetUserByIdCommand,
  GetUserWithContextItemBasedItemRecommendationsCommand,
  ListAllAccountsCommand,
  ListAllDatabasesCommand,
  ListAllItemPropertiesCommand,
  ListAllRatingsOfOneUserCommand,
  ListAllUserPropertiesCommand,
  ListInteractionsOfAllUsersCommand,
  ListInteractionsOfOneUserCommand,
  ListPropertiesOfAllItemsCommand,
  ListPropertiesOfAllUsersCommand,
  ListPropertiesOfManyItemsByIdCommand,
  ListRatingsOfAllUsersCommand,
  ListRecentBackgroundTasksCommand,
  LoginIndividualCommand,
  LoginServiceCommand,
  PartialUpdateIndividualAccountCommand,
  RecommendationClient,
  RenewLoginWithRefreshTokenCommand,
  ResetPasswordCommand,
  ResolveOneAnonymousSessionCommand,
  SendResetPasswordEmailCommand,
  UpdateItemPropertiesInBulkCommand,
  UpdateUserPropertiesInBulkCommand,
  flexibleIdentifiers,
  isAPIErrorGuard,
  persistCredentialsInLocalStorage,
  propertyTypes
};
